import {
  Col,
  DatePicker,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Typography,
  UploadFile,
} from 'antd';
import Upload, { RcFile, UploadChangeParam } from 'antd/es/upload';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";

import { useGetInfoByUserCode } from '../../../api/profile';
import {
  getExchangeRate,
  useCreateSendMoney,
  useGetConfigurationFee,
  useGetExchangeRate,
  useUpdateSendMoney,
} from '../../../api/send-money';
import { useGetAvlBalanceInternal } from '../../../api/withdrawal';
import { ROOT_URL } from '../../../App';
import CloudArrowUp from '../../../common/images/png/CloudArrowUp.png';
import CloudArrowUpDisabled from '../../../common/images/png/CloudArrowUpDisabled.png';
import { ReactComponent as CalenderIcon } from '../../../common/images/svg/CalendarIcon.svg';
import { ReactComponent as CaretDown } from '../../../common/images/svg/CaretDown.svg';
import { ReactComponent as CheckIcon } from '../../../common/images/svg/Check.svg';
import { ReactComponent as CheckSelectIcon } from '../../../common/images/svg/CheckSelect.svg';
import smartLogoDefault from '../../../common/images/svg/smart-logo-icon.svg';
import { ReactComponent as ArrowIcon } from '../../../common/images/svg/SuffixSelectIcon.svg';
import { ReactComponent as TrashIcon } from '../../../common/images/svg/Trash.svg';
import { ReactComponent as ClearIcon } from '../../../common/images/svg/XCircle.svg';
import { CustomButton } from '../../../components/button/buttonCustom';
import { optionPurpostOfRemitter } from '../../../components/inputs/select/purposeOfRemittance.constants';
import LoadingOverlay from '../../../components/loading/loadingOverlay';
import ConfirmModal from '../../../components/modal/confirmModal';
import { useUserDetails } from '../../../components/pages/auth/components/auth/hooks';
import { TOKEN_KEY, USER_DETAILS_KEY } from '../../../components/providers/authProvider';
import { useNotification } from '../../../components/providers/notification/useNotification';
import { RadioCustom } from '../../../components/radio/radio-custom';
import PaymentStatus from '../../../types/paymentStatus.type';
import {
  acceptUploadFileTypes,
  allowedUploadFileTypesMessage,
  SUPPORT_DOC_ALLOWED_EXT,
  SUPPORT_DOC_ALLOWED_MIMES,
} from '../../../utils/acceptUploadFileTypes';
import { getUniqueFileName } from '../../../utils/beforeUpload';
import { clearFieldErrorMessageOnChange } from '../../../utils/form.constants';
import useResponsive from '../../../utils/hooks/useResponsive';
import {
  formatNumber,
  formatNumberV2,
  formatNumberOnChangeInput,
  parseFormattedNumberInput,
  roundTo,
  roundUp,
} from '../../../utils/list.utils';
import { IDocumentsChanges } from '../../components/interface';
import { ButtonTransferMode } from '../../home/component/button-transfer-mode';
import CompanyDefault from './company-default';
import {
  Fiat,
  FormTransferProps,
  listMethod,
  listPayFee,
  listPaymentMethod,
  listTransferMode,
} from './form-transfer-props';
import SelectBankAccount from './select-bank-account';
import SelectBankAccountProvider from './select-bank-account-provider';
import SelectBeneficiary from './select-beneficiary';
import SelectWallet from './select-wallet';
import SelectWalletProvider from './select-wallet-provider';
import TextFee from './text-fee';

const { TextArea } = Input
const { Option, OptGroup } = Select

const MAX_SIZE = 10 * 1024 * 1024
const MAX_FILE_COUNT = 5

export const Direction = { PAY_TO_BEN: 'PAY_TO_BEN', BEN_TO_PAY: 'BEN_TO_PAY' }

// Helper function to find currency by symbol
const findCurrencyBySymbol = (dataAllCurrency: any[], symbol: string) => {
  return dataAllCurrency.find((item: any) => item.symbol === symbol) as any
}

// Helper function to get default value for currency
const getDefaultCurrencyValue = (
  currencyId: number | undefined,
  currencies: any,
  hasExistingValue: boolean,
  existingValue: string,
  firstCalculate?: boolean
) => {
  if (!currencyId) return '0'

  const currencyETH = findCurrencyBySymbol(currencies, 'ETH')
  const currencyUSD = findCurrencyBySymbol(currencies, 'USD')
  const currencyUSDT = findCurrencyBySymbol(currencies, 'USDT')
  const currencyDAI = findCurrencyBySymbol(currencies, 'DAI')
  const currencyEUR = findCurrencyBySymbol(currencies, 'EUR')
  const currencyHKD = findCurrencyBySymbol(currencies, 'HKD')
  const currencyAUD = findCurrencyBySymbol(currencies, 'AUD')
  const currencyJPY = findCurrencyBySymbol(currencies, 'JPY')
  const currencyCHF = findCurrencyBySymbol(currencies, 'CHF')
  const currencyCNY = findCurrencyBySymbol(currencies, 'CNY')
  const currencyAED = findCurrencyBySymbol(currencies, 'AED')
  const currencySGD = findCurrencyBySymbol(currencies, 'SGD')
  const currencyGBP = findCurrencyBySymbol(currencies, 'GBP')

  if (hasExistingValue) return existingValue

  if (firstCalculate) {
    return '0.00'
  }

  switch (currencyId) {
    case currencyUSD?.id:
      return '1000.00'
    case currencyEUR?.id:
      return '1000.00'
    case currencyHKD?.id:
    case currencyAUD?.id:
    case currencyJPY?.id:
    case currencyCHF?.id:
    case currencyCNY?.id:
    case currencyAED?.id:
    case currencySGD?.id:
    case currencyGBP?.id:
      return '1000.00'
    case currencyETH?.id:
      return '1.000000'
    case currencyUSDT?.id:
    case currencyDAI?.id:
      return '1000.0000'
    default:
      return '0'
  }
}

const requiredFields = [
  'beneficiary',
  'send_date',
  'beneficiary_value',
  'payment_value',
  'purposeOfRemittance',
]

const FormTransferV2 = ({
  orderId,
  editData,
  setEditData,
  loadingListWallet,
  loadingListBankAccount,
  form,
  paymentMethod,
  setPaymentMethod,
  setStepNewBeneficiary,
  paramsBeneficiary,
  setParamsBeneficiary,
  dataListCompany,
  beneficiarySelected,
  setBeneficiarySelected,
  dataListWallet,
  paramsWallet,
  setParamsWallet,
  walletSelected,
  setWalletSelected,
  dataListBankAccount,
  paramsBankAccount,
  setParamsBankAccount,
  bankAccountSelected,
  setBankAccountSelected,
  sendCurrency,
  setSendCurrency,
  paymentCurrency,
  setPaymentCurrency,
  dataCompaniesDefault,
  companyDefaultSelected,
  setCompanyDefaultSelected,
  setSearchDefaultCompany,
  paramsWalletProvider,
  setParamsWalletProvider,
  walletSelectedProvider,
  setWalletSelectedProvider,
  dataListWalletProvider,
  paramsBankAccountProvider,
  setParamsBankAccountProvider,
  bankAccountSelectedProvider,
  setBankAccountSelectedProvider,
  dataListBankAccountProvider,
  getDetailSendMoney,
  setOpenAddWallet,
  setOpenAddBankAccount,
  loadingDetail,
  socket,
  companyDefaultCurrency,
  loadingCompanyCurrency,
  loadingDefaulCompany,
  dataAllCurrency,
  onChangeDisabledPortfolio,
  reloadRateFeeKey,
  handleChangeBeneficiary,
}: FormTransferProps) => {
  const navigate = useNavigate();
  const { isTablet } = useResponsive()
  const { open: openNotification } = useNotification()
  const datePickerRef = useRef<any>(null)
  const isSettingDefaultRef = useRef(false)
  const hasSetDefaultRef = useRef(false)
  const isChangingCurrencyRef = useRef(false)
  const hasUserEditedInputRef = useRef(false)
  const hasChangingCurrencyRef = useRef(false)
  // Ref to preserve beneficiary_value error during paymentCurrency change
  const preservedBeneficiaryErrorRef = useRef<string | null>(null)
  // Refs to track if user has input into fields (only validate if user has input)
  const hasInputBeneficiaryRef = useRef(false)
  const hasInputPaymentRef = useRef(false)
  const lastFilledEditIdRef = useRef<number | null>(null)
  const hasSetProviderFromEditRef = useRef(false)
  const hasLoadedRateFeeForEditRef = useRef(false)
  const hasSetDefaultTransferModeRef = useRef(false)
  const isInitialFeePayerRef = useRef(true)

  const { smartUID: IDUser } = useUserDetails()
  // Default to sender; will sync from editData.feePayer / editData.feesPayer in useEffect below
  const [checked, setChecked] = useState(listPayFee[0].value)
  const [sendCurrencySymbol, setSendCurrencySymbol] = useState('USD')
  const [paymentCurrencySymbol, setPaymentCurrencySymbol] = useState('EUR')
  const [disabledPortfolio, setDisabledPortfolio] = useState(false)
  const [created, setCreated] = useState(false)
  const [detailId, setDetailId] = useState(0)
  const [lastChange, setLastChange] = useState('BENEFICIARY')

  useEffect(() => {
    onChangeDisabledPortfolio?.(disabledPortfolio)
  }, [disabledPortfolio, onChangeDisabledPortfolio])

  const [transferMode, setTransferMode] = useState(
    listTransferMode.CRYPTO_NETWORK
  )
  const [searchDate, setSearchDate] = useState<any>(dayjs())
  const [method, setMethod] = useState<any>(listMethod.FIAT_FIAT)
  const [openCompanyDefault, setOpenCompanyDefault] = useState(false)

  const [listBeneficiary, setListBeneficiary] = useState([])
  const [listWallet, setListWallet] = useState([])
  const [listBankAccount, setListBankAccount] = useState([])
  const [listWalletProvider, setListWalletProvider] = useState([])
  const [listBankAccountProvider, setListBankAccountProvider] = useState([])
  const [infoByUserCode, setInfoByUserCode] = useState<any>(null)
  const [configFee, setConfigFee] = useState<any>(null)
  const [transferFee, setTransferFee] = useState<any>(null)
  const [feeConfigId, setFeeConfigId] = useState<any>(undefined)
  const [serviceFee, setServiceFee] = useState<any>(0)
  const [paymentAmount, setPaymentAmount] = useState(0)
  const [beneficiaryAmount, setBeneficiaryAmount] = useState(0)
  const [paymentDecimal, setPaymentDecimal] = useState(2)
  const [beneficiaryDecimal, setBeneficiaryDecimal] = useState(2)
  const [exchangeRate, setExchangeRate] = useState(1)
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(false)
  const [showReviewFee, setShowReviewFee] = useState(false)
  const [firstCalculate, setFirstCalculate] = useState(true)
  const [beneficiaryValueError, setBeneficiaryValueError] = useState(false)
  const [paymentValueError, setPaymentValueError] = useState(false)
  const [isFirstLoadBalance, setIsFirstLoadBalance] = useState(true)
  const [direction, setDirection] = useState(Direction.PAY_TO_BEN)
  const [disabledProvider, setDisabledProvider] = useState(false)
  const [purpose, setPurpose] = useState(false)
  const [loadingDefaultValue, setLoadingDefaultValue] = useState(true)
  const [showBalanceConfirmation, setShowBalanceConfirmation] = useState(false)
  const [dataEditCreateOrder, setDataEditCreateOrder] = useState(null)
  const details = localStorage.getItem(USER_DETAILS_KEY);

  const specialIntructionsDraft = `Ref ${IDUser ?? JSON.parse(details ?? '{}')?.smartUID ?? ''}`
  const payment_value = Form.useWatch('payment_value', form)
  const beneficiary_value = Form.useWatch('beneficiary_value', form)
  const beneficiary = Form.useWatch('beneficiary', form)
  const beneficiary_bank_account = Form.useWatch(
    'beneficiary_bank_account',
    form
  )

  const beneficiary_wallet_address = Form.useWatch(
    'beneficiary_wallet_address',
    form
  )

  const smartUID = Form.useWatch('smartUID', form)
  const providerBankAccount = Form.useWatch('providerBankAccount', form)
  const providerWallet = Form.useWatch('providerWallet', form)
  const purposeOfRemittance = Form.useWatch('purposeOfRemittance', form)
  const send_date = Form.useWatch('send_date', form)

  const [document, setDocument] = useState<UploadChangeParam<UploadFile<any>>>({
    file: {} as UploadFile<any>,
    fileList: [],
  })

  const { data: avlBalance, refetch: refetchBalance } =
    useGetAvlBalanceInternal(Number(paymentCurrency || 0))

  const {
    data: rate,
    isLoading: loadingRate,
    refetch: refetchRate,
  } = useGetExchangeRate({ from: paymentCurrency, to: sendCurrency })

  const { mutate: fetchInfoByUserCode, isLoading: loadingUserCode } =
    useGetInfoByUserCode({
      onSuccess: (response: { data: { data: any } }) => {
        setInfoByUserCode(response?.data?.data)
        form.setFields([{ name: 'smartUID', errors: undefined }])
      },
      onError: (error: { message: string; reason_code: string }) => {
        setInfoByUserCode(
          error?.reason_code === 'ERR00010'
            ? 'This SMART UID does not exist in SMART+'
            : error?.message
        )
        if (error?.reason_code === 'ERR00010') {
          form.setFields([
            {
              name: 'smartUID',
              errors: ['This SMART UID does not exist in SMART+'],
            },
          ])
        } else {
          form.setFields([{ name: 'smartUID', errors: [error?.message || ''] }])
        }
      },
    })

  const { mutate: fetConfigurationFee, isLoading: loadingFee } =
    useGetConfigurationFee({
      onError: () => {
        if (openNotification) {
          openNotification({
            key: 'fee_error',
            message: 'Something went wrong! Please try again in a few minutes.',
            type: 'error',
          })
        }
      },
    })

  const fetchFee = (currencyId: number | undefined) => {
    fetConfigurationFee(currencyId, {
      onSuccess: (res) => {
        if (currencyId === sendCurrency) {
          setConfigFee(res.data.data)
        }
      },
      onError: () => {
        // Error handled by useGetConfigurationFee hook
      },
    })
  }

  useEffect(() => {
    if (!sendCurrency || !paymentCurrency) return

    const refreshRateAndFee = () => {
      refetchRate()
      fetchFee(sendCurrency)
    }

    // Initial fetch
    refreshRateAndFee()

    // Poll every 60s
    const intervalId = setInterval(() => {
      refreshRateAndFee()
    }, 60000)

    return () => clearInterval(intervalId)
  }, [sendCurrency, paymentCurrency, reloadRateFeeKey, editData?.id, detailId, created])

  const { mutate: createSendMoney, isLoading: loadingCreate } =
    useCreateSendMoney({
      onSuccess: (response: { data: { message: string; data: any } }) => {
        getDetailSendMoney(response?.data?.data?.id)
        setCreated(true)
        setDetailId(response?.data?.data?.id)
        if (openNotification) {
          openNotification({
            key: 'create_success',
            message: 'Order submitted successfully!',
            type: 'success',
          })
        }
      },
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === 'ERR00104') {
          setShowReviewFee(true)
        } else if (openNotification) {
          openNotification({
            key: 'create_failed',
            message: error.message || 'Create order failed',
            type: 'error',
          })
        }
      },
    })

  const { mutate: updateSendMoney, isLoading: loadingUpdate } =
    useUpdateSendMoney({
      onSuccess: (response: { data: { message: string; data: any } }) => {
        getDetailSendMoney(response?.data?.data?.id)
        setCreated(true)
        setDetailId(response?.data?.data?.id)
        // setEditData(response?.data?.data)
        if (openNotification) {
          openNotification({
            key: 'update_success',
            message: 'Order updated successfully!',
            type: 'success',
          })
        }
      },
      onError: (error: { message: string; reason_code: string }) => {
        if (error?.reason_code === 'ERR00120') {
          if (openNotification) {
            openNotification({
              key: 'update_failed',
              message: error.message || 'Update order failed',
              type: 'error',
            })
          }
          const isFiat = Fiat.includes(paymentCurrencySymbol);
          navigate(`/history?tab=${isFiat ? "fiat" : "crypto"}&orderId=${orderId}&type=send-money`);
        } else if (error?.reason_code === 'ERR00104') {
          setShowReviewFee(true)
        } else if (openNotification) {
          openNotification({
            key: 'update_failed',
            message: error.message || 'Update order failed',
            type: 'error',
          })
        }
      },
    })

  const onSubmitOrder = (values: any) => {
    const fileList = values?.supportDocument?.fileList ?? []
    const documents = fileList
      .filter((f: UploadFile) => f.status === 'done' && f.response)
      .map((f: UploadFile) => f.response as string)
    const specialInstructions = values?.special_intructions
    const specialInstructionsDetail =
      specialInstructions &&
        specialInstructions.startsWith(specialIntructionsDraft)
        ? (specialInstructions.slice(specialIntructionsDraft.length) ??
          undefined)
        : specialInstructions || undefined

    // Normalize numeric amounts to correct decimals before create
    const normalizedPaymentAmount = Number(
      paymentAmount.toFixed(paymentDecimal ?? 2)
    )

    const normalizedBeneficiaryAmount = Number(
      beneficiaryAmount.toFixed(beneficiaryDecimal ?? 2)
    )

    const body = {
      senderAmount: normalizedPaymentAmount,
      receiverAmount: normalizedBeneficiaryAmount,
      direction,
      companyId: beneficiarySelected?.id,
      bankAccountId: bankAccountSelected?.id,
      receiveCurrencyId: sendCurrency,
      paymentCurrencyId: paymentCurrency,
      specialInstructions: specialIntructionsDraft ?? undefined,
      specialInstructionsDetail,
      documents,
      providerWalletId: walletSelectedProvider?.id,
      senderMethod: 'INTERNAL',
      receiverMethod:
        transferMode === listTransferMode.INTERNAL_TRANSFER
          ? 'INTERNAL'
          : 'EXTERNAL',
      feesPayer: checked === listPayFee[0].value ? 'SENDER' : 'BENEFICIARY',
      feeConfigVersion: configFee?.version,
      dateOfTransaction: values?.send_date,
      serviceProviderId: companyDefaultSelected?.id,
      receiverUserId:
        transferMode === listTransferMode.INTERNAL_TRANSFER
          ? infoByUserCode?.id
          : undefined,
      providerBankAccountId: bankAccountSelectedProvider?.id || undefined,
      beneficiaryWalletId: walletSelected?.id || undefined,
      feeConfigId,
      exchangeRate,
      usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate,
      status: PaymentStatus.CREATED,
      purposeOfRemittance: values.purposeOfRemittance,
    }
    createSendMoney(body)
  }

  const onUpdateOrder = (values: any) => {
    const fileList = values?.supportDocument?.fileList ?? []
    const initialDocumentNames = (editData?.documentNames ?? [])
      .map((d: any) => (typeof d === 'string' ? d : d?.name))
      .filter(Boolean) as string[]

    // new: only newly uploaded files (have response URL); new and deleted can share the same name
    const newUrls = fileList
      .filter((f: UploadFile) => f.status === 'done' && f.response)
      .map((f: UploadFile) => f.response as string)

    // deleted: initial documents that are no longer in the list as *existing* items (uid starts with 'existing-')
    // Compare by existing-uid + name so that if user removes "doc.pdf" and adds another "doc.pdf", we send one deleted and one new
    const deletedNames = initialDocumentNames.filter(
      (initialName) =>
        !fileList.some(
          (f: UploadFile) =>
            String(f.uid).startsWith('existing-') && f.name === initialName
        )
    )

    const documentChanges: IDocumentsChanges = {
      new: newUrls.length ? newUrls : [],
      deleted: deletedNames.length ? deletedNames : [],
    }
    const specialInstructions = values?.special_intructions
    const specialInstructionsDetail =
      specialInstructions &&
        specialInstructions.startsWith(specialIntructionsDraft)
        ? (specialInstructions.slice(specialIntructionsDraft.length) ??
          undefined)
        : specialInstructions || undefined
    const id = orderId || editData?.id || detailId
    if (!id) return openNotification?.({
      key: 'update_failed',
      message: 'Order Id not found',
      type: 'error',
    });
    // Normalize numeric amounts to correct decimals before update
    const normalizedPaymentAmount = Number(
      paymentAmount.toFixed(paymentDecimal ?? 2)
    )

    const normalizedBeneficiaryAmount = Number(
      beneficiaryAmount.toFixed(beneficiaryDecimal ?? 2)
    )

    const body = {
      id: Number(id),
      senderAmount: normalizedPaymentAmount,
      receiverAmount: normalizedBeneficiaryAmount,
      direction,
      companyId: beneficiarySelected?.id,
      bankAccountId: bankAccountSelected?.id,
      receiveCurrencyId: sendCurrency,
      paymentCurrencyId: paymentCurrency,
      specialInstructions: specialIntructionsDraft ?? undefined,
      specialInstructionsDetail,
      documentChanges,
      providerWalletId: walletSelectedProvider?.id,
      senderMethod: 'INTERNAL',
      receiverMethod:
        transferMode === listTransferMode.INTERNAL_TRANSFER
          ? 'INTERNAL'
          : 'EXTERNAL',
      feesPayer: checked === listPayFee[0].value ? 'SENDER' : 'BENEFICIARY',
      feeConfigVersion: configFee?.version,
      dateOfTransaction: values?.send_date,
      serviceProviderId: companyDefaultSelected?.id,
      receiverUserId:
        transferMode === listTransferMode.INTERNAL_TRANSFER
          ? infoByUserCode?.id
          : undefined,
      providerBankAccountId: bankAccountSelectedProvider?.id || undefined,
      beneficiaryWalletId: walletSelected?.id || undefined,
      feeConfigId,
      exchangeRate,
      usdToFeeCurrencyRate: configFee?.usdToFeeCurrencyRate,
      purposeOfRemittance: values.purposeOfRemittance,
    }
    updateSendMoney(body)
  }

  // Handler to validate all fields before submitting
  // This is called by onFinish when form is submitted
  const handleSubmitOrder = async (values: any) => {
    try {
      hasInputBeneficiaryRef.current = true
      hasInputPaymentRef.current = true

      // Determine beneficiary-specific required fields based on transfer mode
      const getBeneficiaryRequiredFields = () => {
        const baseFields = [...requiredFields]

        if (transferMode === listTransferMode.CRYPTO_NETWORK) {
          return [...baseFields, 'beneficiary_wallet_address']
        }
        if (transferMode === listTransferMode.BANK_TRANSFER) {
          return [...baseFields, 'beneficiary_bank_account']
        }
        return [...baseFields, 'smartUID']
      }

      // Determine payment-specific required fields based on payment method
      const getPaymentRequiredFields = (beneficiaryFields: string[]) => {
        if (paymentMethod !== listPaymentMethod.DIRECTER_TRANSFER) {
          return beneficiaryFields
        }

        const currency = dataAllCurrency.find(
          (item: any) => item.id === paymentCurrency
        )
        const currencyType = currency?.type

        const additionalFields = ['companies_default']
        if (currencyType === 'FIAT') {
          additionalFields.push('providerBankAccount')
        } else {
          additionalFields.push('providerWallet')
        }
        return [...beneficiaryFields, ...additionalFields]
      }

      const beneficiaryRequiredFields = getBeneficiaryRequiredFields()
      const fieldsToValidate = getPaymentRequiredFields(
        beneficiaryRequiredFields
      )

      // Validate all required fields
      await form.validateFields(fieldsToValidate)

      // Check for validation errors
      const hasFieldErrors =
        form.getFieldError('beneficiary_value').length > 0 ||
        form.getFieldError('payment_value').length > 0 ||
        form.getFieldError('smartUID').length > 0 ||
        beneficiaryValueError ||
        paymentValueError
      if (hasFieldErrors) {
        throw new Error('Validation failed')
      }

      if (
        !walletSelectedProvider &&
        !companyDefaultSelected
      ) {
        if (openNotification) {
          openNotification({
            key: 'fee_error',
            message: 'Please select provider.',
            type: 'error',
          })
        }
        throw new Error('Validation failed')
      }
      if (disabledPortfolio) {
        setShowBalanceConfirmation(true)
        setDataEditCreateOrder(values)
        return
      }
      // All validations passed, proceed with submission
      // If we already have a detailId (order created) or editData.id, go to update flow
      if (orderId || editData?.id || detailId) {
        onUpdateOrder(values)
      } else {
        onSubmitOrder(values)
      }
    } catch (errorInfo: any) {
      console.log('Validation failed:', errorInfo)
    }
  }

  const handleRemoveFile = (fileToRemove: UploadFile) => {
    const newFileList = document.fileList.filter(
      (file) => file.uid !== fileToRemove.uid
    )
    if (newFileList.length === 0) {
      form.setFieldValue('supportDocument', undefined)
      form.validateFields(['supportDocument'])
    }
    setDocument({ file: {} as UploadFile<any>, fileList: newFileList })
    form.setFieldValue('supportDocument', {
      file: {} as UploadFile<any>,
      fileList: newFileList,
    })
  }

  let uploadErrorShown = false

  const handleChangeFile = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file, fileList } = info

    const existingFiles: UploadFile[] =
      form.getFieldValue('supportDocument')?.fileList ?? []
    const totalFiles = existingFiles.length
    if (totalFiles > MAX_FILE_COUNT) {
      if (!uploadErrorShown) {
        openNotification?.({
          key: 'file_count',
          message: `You can upload up to ${MAX_FILE_COUNT} files only!`,
          type: 'error',
        })
      }
      uploadErrorShown = true
      setTimeout(() => {
        uploadErrorShown = false
      }, 500)
      form.setFieldValue('supportDocument', document)
      return
    }
    setDocument({ file, fileList })
    form.setFieldValue('supportDocument', { file, fileList })
  }

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    const isValidType =
      SUPPORT_DOC_ALLOWED_MIMES.has(file.type) ||
      (SUPPORT_DOC_ALLOWED_EXT.has(ext) &&
        (!file.type || file.type === 'application/octet-stream'))

    if (!isValidType) {
      openNotification?.({
        key: 'file_type',
        message: allowedUploadFileTypesMessage,
        type: 'error',
      })
      return Upload.LIST_IGNORE
    }

    if (file.size > MAX_SIZE) {
      openNotification?.({
        key: 'file_size',
        message: `${file.name} is too big, max size is 10 MB!`,
        type: 'error',
      })
      return Upload.LIST_IGNORE
    }

    const currentFiles: UploadFile[] =
      form.getFieldValue('supportDocument')?.fileList ?? []

    return getUniqueFileName(file, currentFiles)
  }

  const handleSearchDate = (
    _date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    setSearchDate(Array.isArray(dateString) ? dateString[0] : dateString)
  }

  const handleContinueReview = () => {
    fetchFee(sendCurrency)
    setShowReviewFee(false)
  }

  const onOpenChange = useCallback((open: boolean) => {
    if (disabledProvider) return
    setOpenCompanyDefault(open)
  }, [])

  const roundDown = useCallback((value: number, decimals: number) => {
    const factor = Math.pow(10, decimals)
    return Math.floor(value * factor) / factor
  }, [])

  /**
   * Round down a value with minimum precision guarantee.
   * If the result is 0.00, increases decimal places until a non-zero value is found.
   * @param value - The value to round down
   * @param decimals - Initial decimal places (default: 2)
   * @param maxDecimals - Maximum decimal places to try (default: 18)
   * @returns The rounded down value with appropriate precision
   */
  const roundDownWithMinimum = useCallback(
    (
      value: number,
      decimals = 2,
      maxDecimals = 18
    ): { value: number; decimals: number } => {
      let currentDecimals = decimals
      let result = roundDown(value, currentDecimals)

      // If result is 0 and value is not 0, increase decimal places
      while (result === 0 && value !== 0 && currentDecimals < maxDecimals) {
        currentDecimals += 2 // Increase by 2 decimal places each time
        result = roundDown(value, currentDecimals)
      }

      return { value: result, decimals: currentDecimals }
    },
    [roundDown]
  )

  const hasRecalcPaymentFromEditRef = useRef(false)

  useEffect(() => {
    if (!rate) return
    setExchangeRate(rate?.data?.data)
  }, [rate])

  useEffect(() => {
    if (!editData?.id) {
      lastFilledEditIdRef.current = null
      hasSetProviderFromEditRef.current = false
      hasRecalcPaymentFromEditRef.current = false
      hasLoadedRateFeeForEditRef.current = false
      setDocument({ file: {} as UploadFile<any>, fileList: [] })
      form.setFieldValue('supportDocument', undefined)
    }
  }, [editData?.id, form])

  // Sync document list from editData whenever editData changes (e.g. after Update Order → Details → Edit)
  // so we don't re-send the same documentChanges.new; server list becomes the single source of truth
  useEffect(() => {
    if (!editData?.id || editData?.documentNames === undefined) return

    const docNames = (editData.documentNames ?? []) as (string | { name?: string })[]
    const names = docNames
      .map((d) => (typeof d === 'string' ? d : d?.name))
      .filter(Boolean) as string[]

    const initialFileList: UploadFile[] = names.map((name: string, index: number) => ({
      uid: `existing-${editData.id}-${index}-${name}`,
      name,
      status: 'done' as const,
    }))
    setDocument({ file: {} as UploadFile<any>, fileList: initialFileList })
    form.setFieldValue('supportDocument', {
      file: {} as UploadFile<any>,
      fileList: initialFileList,
    })
  }, [editData?.id, editData?.documentNames, form])

  // Fill form and state from order detail when editing (URL ?orderId= or after Create then Edit)
  useEffect(() => {
    if (!editData?.id || !editData?.amounts || !dataAllCurrency?.length) return
    if (lastFilledEditIdRef.current === editData.id) return
    lastFilledEditIdRef.current = editData.id

    const order = editData
    const receiveSymbol = order.receiveCurrency
    const paymentSymbol = order.paymentCurrency
    const sendCurrencyCur = findCurrencyBySymbol(dataAllCurrency, receiveSymbol)
    const paymentCurrencyCur = findCurrencyBySymbol(dataAllCurrency, paymentSymbol)
    if (!sendCurrencyCur || !paymentCurrencyCur) return

    const receiverAmount = Number(order.amounts?.receiverAmount ?? 0)
    const senderAmount = Number(order.amounts?.senderAmount ?? 0)
    const orderDirection: string = order.direction ?? Direction.BEN_TO_PAY
    const isPayToBen = orderDirection === Direction.PAY_TO_BEN

    setSendCurrency(sendCurrencyCur.id)
    setSendCurrencySymbol(sendCurrencyCur.symbol)
    setBeneficiaryDecimal(sendCurrencyCur.decimals ?? 2)
    setPaymentCurrency(paymentCurrencyCur.id)
    setPaymentCurrencySymbol(paymentCurrencyCur.symbol)
    setPaymentDecimal(paymentCurrencyCur.decimals ?? 2)
    setDetailId(order.id)
    setCreated(true)
    // Reset edit flags so initial recalculation does not override API amounts
    hasUserEditedInputRef.current = false
    const sendDate = order.timestamps?.dateOfTransaction
    const benDec = sendCurrencyCur.decimals ?? 2
    const payDec = paymentCurrencyCur.decimals ?? 2

    if (isPayToBen) {
      // PAY_TO_BEN: keep payment_value (senderAmount), recalc beneficiary_value from rate
      setPaymentAmount(senderAmount)
      setBeneficiaryAmount(0)
      setLastChange('SENDER')
      setDirection(Direction.PAY_TO_BEN)
      form.setFieldsValue({
        payment_value: formatNumberV2(senderAmount, payDec),
        beneficiary_value: undefined,
        beneficiary_currency: sendCurrencyCur.id,
        payment_currency: paymentCurrencyCur.id,
        send_date: sendDate ? dayjs(sendDate) : undefined,
        purposeOfRemittance: order.purposeOfRemittance || undefined,
        special_intructions: order.specialInstructions + (order.specialInstructionsDetail ?? '') || undefined,
      })
    } else {
      // BEN_TO_PAY: keep beneficiary_value (receiverAmount), recalc payment_value from rate
      setBeneficiaryAmount(receiverAmount)
      setPaymentAmount(0)
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)
      form.setFieldsValue({
        beneficiary_value:
          receiverAmount !== undefined && receiverAmount !== null
            ? formatNumberV2(receiverAmount, benDec)
            : undefined,
        payment_value: undefined,
        beneficiary_currency: sendCurrencyCur.id,
        payment_currency: paymentCurrencyCur.id,
        send_date: sendDate ? dayjs(sendDate) : undefined,
        purposeOfRemittance: order.purposeOfRemittance || undefined,
        special_intructions: order.specialInstructions + (order.specialInstructionsDetail ?? '') || undefined,
      })
    }

    if (order.receiveUser) {
      setTransferMode(listTransferMode.INTERNAL_TRANSFER)
      form.setFieldValue('smartUID', order.receiveUser?.smartUID)
    } else if (order.beneficiary?.bankName || order.beneficiary?.bankAccount) {
      setTransferMode(listTransferMode.BANK_TRANSFER)
    } else {
      setTransferMode(listTransferMode.CRYPTO_NETWORK)
    }

    const remitterType = order.remiter?.type ?? order.remitter?.type
    if (remitterType === 'INTERNAL') {
      setPaymentMethod(listPaymentMethod.PORTFOLIO)
    } else {
      setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
    }

    // Fill supporting documents from order when editing (show existing, allow delete)
    const docNames = order.documentNames ?? []
    const initialFileList: UploadFile[] = docNames
      .map((d: string | { name?: string }) => (typeof d === 'string' ? d : d?.name))
      .filter(Boolean)
      .map((name: string, index: number) => ({
        uid: `existing-${order.id}-${index}-${name}`,
        name,
        status: 'done' as const,
      }))
    setDocument({ file: {} as UploadFile<any>, fileList: initialFileList })
    form.setFieldValue('supportDocument', {
      file: {} as UploadFile<any>,
      fileList: initialFileList,
    })

    setLoadingDefaultValue(false)
    hasSetDefaultRef.current = true
  }, [editData, dataAllCurrency, form])

  useEffect(() => {
    if (!editData?.id || !editData?.amounts) return
    if (loadingRate) return

    // Edit mode: use latest rate from API so recalc uses e.g. 1.18, not stale exchangeRate state (e.g. still 1)
    const rateFromApi = rate?.data?.data ?? rate?.data
    const effectiveRate =
      editData?.id &&
        rateFromApi !== undefined &&
        rateFromApi !== null &&
        Number(rateFromApi) > 0
        ? Number(rateFromApi)
        : exchangeRate
    if (!effectiveRate || effectiveRate <= 0) return
    if (hasRecalcPaymentFromEditRef.current) return

    // Always base recalculation on order data, not on possibly-mutated state
    const orderDirection: string = editData.direction ?? Direction.BEN_TO_PAY
    const isPayToBen = orderDirection === Direction.PAY_TO_BEN

    if (isPayToBen) {
      // PAY_TO_BEN: keep payment_value (senderAmount), recalc beneficiary_value
      const senderAmount = Number(editData.amounts?.senderAmount ?? 0)
      if (!senderAmount || senderAmount <= 0) return

      const benDec = beneficiaryDecimal ?? 2
      const calculatedBeneficiary = senderAmount * effectiveRate
      const beneficiaryValueStr = Number(calculatedBeneficiary).toFixed(benDec)

      hasRecalcPaymentFromEditRef.current = true
      setBeneficiaryAmount(Number(beneficiaryValueStr))
      form.setFieldsValue({ beneficiary_value: beneficiaryValueStr })
    } else {
      // BEN_TO_PAY: keep beneficiary_value (receiverAmount), recalc payment_value
      const receiverAmount = Number(editData.amounts?.receiverAmount ?? 0)
      if (!receiverAmount || receiverAmount <= 0) return

      const payDec = paymentDecimal ?? 2
      const calculatedPayment = receiverAmount / effectiveRate
      const paymentValueStr = Number(calculatedPayment).toFixed(payDec)

      hasRecalcPaymentFromEditRef.current = true
      setPaymentAmount(Number(paymentValueStr))
      form.setFieldsValue({ payment_value: paymentValueStr })
    }
  }, [
    editData?.id,
    editData?.direction,
    editData?.amounts?.receiverAmount,
    editData?.amounts?.senderAmount,
    editData?.amounts?.totalReceiveAmount,
    exchangeRate,
    loadingRate,
    paymentDecimal,
    beneficiaryDecimal,
    rate,
    form,
  ])

  // Set provider when editing: run after dataCompaniesDefault has loaded for the order's payment currency
  // API may return top-level provider.provider or remiter.provider / remitter.provider
  // If provider name not found in list (e.g. company removed), clear data
  useEffect(() => {
    const providerName = editData?.provider?.provider ?? editData?.remiter?.provider ?? editData?.remitter?.provider
    if (!editData?.id) return
    if (!providerName) {
      hasSetProviderFromEditRef.current = true
      return
    }
    if (hasSetProviderFromEditRef.current) return
    const list = Array.isArray(dataCompaniesDefault) ? dataCompaniesDefault : dataCompaniesDefault?.items
    if (!Array.isArray(list) || list.length === 0) return
    const name = String(providerName).trim()
    const company = (list as any[]).find((c: any) => {
      const n = c?.name !== undefined && c?.name !== null ? String(c.name).trim() : ''
      const f = c?.fullName !== undefined && c?.fullName !== null ? String(c.fullName).trim() : ''
      return n === name || f === name
    })
    hasSetProviderFromEditRef.current = true
    if (company) {
      setCompanyDefaultSelected(company)
      form?.setFieldValue('companies_default', company?.id)
    } else {
      setCompanyDefaultSelected(null)
      form?.setFieldValue('companies_default', null)
      setBankAccountSelectedProvider(null)
      setWalletSelectedProvider(null)
      form?.resetFields(['providerBankAccount', 'providerWallet'])
    }
  }, [editData?.id, editData?.provider?.provider, editData?.remiter?.provider, editData?.remitter?.provider, dataCompaniesDefault, form])

  // When editing: set or clear provider bank account by id; clear if id not in list (data not loadable)
  useEffect(() => {
    const bankAccountId = editData?.provider?.bankAccountId
    if (
      !editData?.id ||
      bankAccountId === null ||
      bankAccountId === undefined ||
      !companyDefaultSelected?.id
    ) {
      return
    }
    const list = Array.isArray(listBankAccountProvider) ? listBankAccountProvider : []
    const found = (list as any[]).find((item: any) => Number(item?.id) === Number(bankAccountId))
    if (found) {
      setBankAccountSelectedProvider(found)
      form?.setFieldValue('providerBankAccount', found.id)
    } else if (list.length > 0) {
      setBankAccountSelectedProvider(null)
      form?.setFieldValue('providerBankAccount', null)
    }
  }, [
    editData?.id,
    editData?.provider?.bankAccountId,
    listBankAccountProvider,
    companyDefaultSelected?.id,
    form,
  ])

  // When editing: set or clear provider wallet by id; clear if id not in list (data not loadable)
  // API may return walletId, wallet_id or providerWalletId
  useEffect(() => {
    const walletId =
      editData?.provider?.walletId ??
      editData?.provider?.wallet_id ??
      editData?.provider?.providerWalletId
    if (
      !editData?.id ||
      walletId === null ||
      walletId === undefined ||
      !companyDefaultSelected?.id
    ) {
      return
    }
    const list = Array.isArray(listWalletProvider) ? listWalletProvider : []
    const found = (list as any[]).find((item: any) => Number(item?.id) === Number(walletId))
    if (found) {
      setWalletSelectedProvider(found)
      form?.setFieldValue('providerWallet', found.id)
    } else if (list.length > 0) {
      setWalletSelectedProvider(null)
      form?.setFieldValue('providerWallet', null)
    }
  }, [
    editData?.id,
    editData?.provider?.walletId,
    editData?.provider?.wallet_id,
    editData?.provider?.providerWalletId,
    listWalletProvider,
    companyDefaultSelected?.id,
    form,
  ])

  // When editing: match beneficiary bank account / wallet from order once lists are loaded
  // If beneficiary has bankAccountId/walletId but id not in list, clear (data not loadable)
  useEffect(() => {
    if (!editData?.id || !beneficiarySelected) return
    const ben = editData.beneficiary
    if (!ben) return
    const bankItems = Array.isArray(dataListBankAccount?.items) ? dataListBankAccount.items : []
    const walletItems = Array.isArray(dataListWallet?.items) ? dataListWallet.items : []
    if (
      bankItems.length > 0 &&
      (ben.bankAccount || ben.bankName || ben.bankAccountId !== null)
    ) {
      const matchById =
        ben.bankAccountId !== null && ben.bankAccountId !== undefined
          ? (bankItems as any[]).find(
            (item: any) => Number(item?.id) === Number(ben.bankAccountId)
          )
          : null

      const matchByAccount =
        ben.bankAccount || ben.bankName
          ? (bankItems as any[]).find(
            (item: any) =>
              item?.accountNumber === ben.bankAccount ||
              item?.accountNumber === String(ben.bankAccount)
          )
          : null
      const match = matchById ?? matchByAccount

      if (match) {
        setBankAccountSelected(match)
        form?.setFieldValue('beneficiary_bank_account', match.id)
      } else if (
        (ben.bankAccountId !== null && ben.bankAccountId !== undefined) ||
        ben.bankAccount
      ) {
        setBankAccountSelected(null)
        form?.setFieldValue('beneficiary_bank_account', null)
      }
    }

    const benWalletId = ben.walletId ?? ben.wallet_id
    const hasBenWalletId =
      benWalletId !== null && benWalletId !== undefined
    if (
      walletItems.length > 0 &&
      (ben.walletAddress || hasBenWalletId)
    ) {
      const matchById = hasBenWalletId
        ? (walletItems as any[]).find(
          (item: any) => Number(item?.id) === Number(benWalletId)
        )
        : null

      const matchByAddress = ben.walletAddress
        ? (walletItems as any[]).find(
          (item: any) =>
            item?.address === ben.walletAddress ||
            item?.walletAddress === ben.walletAddress
        )
        : null
      const match = matchById ?? matchByAddress
      if (match) {
        setWalletSelected(match)
        form?.setFieldValue('beneficiary_wallet_address', match.id)
      } else if (hasBenWalletId || ben.walletAddress) {
        setWalletSelected(null)
        form?.setFieldValue('beneficiary_wallet_address', null)
      }
    }
  }, [
    editData?.id,
    editData?.beneficiary,
    beneficiarySelected,
    dataListBankAccount?.items,
    dataListWallet?.items,
    form,
  ])

  // 1. Set default currency when dataAllCurrency is loaded
  useEffect(() => {
    if (!dataAllCurrency?.length) return
    if (editData?.id) return
    const beneficiaryDefault = dataAllCurrency.find(
      (item: any) => item.id === sendCurrency
    ) as any

    const paymentDefault = dataAllCurrency.find(
      (item: any) => item.id === paymentCurrency
    ) as any

    const beneficiaryCurrencyDefault =
      beneficiaryDefault ?? findCurrencyBySymbol(dataAllCurrency, 'USD')

    const paymentCurrencyDefault =
      paymentDefault ?? findCurrencyBySymbol(dataAllCurrency, 'EUR')

    // Only proceed if we found the currencies (avoid hardcoded fallback IDs)
    if (
      !beneficiaryCurrencyDefault ||
      !paymentCurrencyDefault ||
      !sendCurrency ||
      !paymentCurrency
    )
      return

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id
    const paymentCurrencyId = paymentCurrencyDefault.id

    form.setFieldsValue({
      beneficiary_currency: beneficiaryCurrencyId,
    })
    setSendCurrency(beneficiaryCurrencyId)
    setSendCurrencySymbol(beneficiaryCurrencyDefault.symbol)
    setBeneficiaryDecimal(beneficiaryCurrencyDefault.decimals)

    form.setFieldsValue({
      payment_currency: paymentCurrencyId,
    })
    setPaymentCurrency(paymentCurrencyId)
    setPaymentCurrencySymbol(paymentCurrencyDefault.symbol)
    setPaymentDecimal(paymentCurrencyDefault.decimals)
  }, [dataAllCurrency, form, sendCurrency, paymentCurrency, editData])

  // 2. Set default values when all required data is loaded
  useEffect(() => {
    if (!dataAllCurrency?.length) return
    if (!rate?.data?.data) return
    if (!paymentDecimal || paymentDecimal === 0) return
    if (!beneficiaryDecimal || beneficiaryDecimal === 0) return
    if (hasSetDefaultRef.current) return // Only set default once
    if (editData?.id) return // Edit mode: values come from fill-from-edit effect

    const beneficiaryCurrencyDefault = findCurrencyBySymbol(
      dataAllCurrency,
      'USD'
    )
    const paymentCurrencyDefault = findCurrencyBySymbol(dataAllCurrency, 'EUR')

    if (!beneficiaryCurrencyDefault || !paymentCurrencyDefault) return

    const beneficiaryCurrencyId = beneficiaryCurrencyDefault.id

    // Check if user has already entered values manually
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    // If user has already entered values, don't set default
    if (beneficiaryValueNum > 0 || paymentValueNum > 0) {
      hasSetDefaultRef.current = true
      setLoadingDefaultValue(false)
      return
    }

    // Set default beneficiary value
    const defaultBeneficiaryValue = getDefaultCurrencyValue(
      beneficiaryCurrencyId,
      dataAllCurrency,
      false,
      '',
      firstCalculate
    )

    const paymentValue = Number(
      (Number(defaultBeneficiaryValue) * (1 / rate?.data?.data)).toFixed(
        paymentDecimal
      )
    )
    // Set direction and lastChange to prevent recalculation in other useEffect
    setDirection(editData?.direction ?? Direction.BEN_TO_PAY)
    setLastChange(
      editData?.direction === Direction.BEN_TO_PAY 
      ? 'BENEFICARY' 
      : editData?.direction === Direction.PAY_TO_BEN 
      ? 'SENDER' 
      : 'BENEFICARY'
    )
    // setDirection(Direction.BEN_TO_PAY)
    // setLastChange('BENEFICARY')

    // Mark that we're setting default values
    isSettingDefaultRef.current = true

    form.setFieldsValue({
      beneficiary_value: defaultBeneficiaryValue,
      payment_value: paymentValue,
    })
    setPaymentAmount(paymentValue)
    setBeneficiaryAmount(Number(defaultBeneficiaryValue))

    // Mark that default has been set and reset isSettingDefault flag
    hasSetDefaultRef.current = true
    setTimeout(() => {
      isSettingDefaultRef.current = false
      setLoadingDefaultValue(false)
    }, 0)
  }, [
    dataAllCurrency,
    rate?.data?.data,
    paymentDecimal,
    beneficiaryDecimal,
    form,
  ])

  useEffect(() => {
    if (!rate) return
    if (!firstCalculate || orderId) return
    const paymentValue = (0 / rate?.data?.data).toFixed(paymentDecimal)
    form.setFieldsValue({
      payment_value: paymentValue,
    })
    setPaymentAmount(Number(paymentValue))
    setFirstCalculate(false)
  }, [rate])

  useEffect(() => {
    if (loadingFee || transferFee === null || serviceFee === null) {
      return
    }
    // IMPORTANT: Don't clear errors when configFee is null (during currency change)
    // or when actively changing currency - this prevents losing validation errors
    if (!configFee) {
      return
    }

    if (paymentAmount > 0 && beneficiaryAmount > 0) {
      if (checked === listPayFee[0].value) {
        const minPayment = roundTo(
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        )

        const minBeneficiary = roundTo(
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        )
        if (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current && !hasChangingCurrencyRef.current) {
          return
        }
        if (paymentAmount < minPayment) {
          setBeneficiaryValueError(true)
          const errorMessage = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
          form.setFields([
            {
              name: 'beneficiary_value',
              errors: [errorMessage],
            },
          ])
        } else {
          setBeneficiaryValueError(false)
          // Only clear min amount errors, keep other validation errors (like max limit)
          const currentErrors = form.getFieldError('beneficiary_value')
          // Filter out min amount errors, keep other errors
          const otherErrors = currentErrors.filter(
            (err: string) => !err?.includes('must be at least')
          )
          if (otherErrors.length === 0) {
            form.setFields([{ name: 'beneficiary_value', errors: [] }])
          } else {
            form.setFields([{ name: 'beneficiary_value', errors: otherErrors }])
          }
        }
      } else {
        const minBeneficiary = roundTo(
          transferFee +
          serviceFee +
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        )
        if (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current && !hasChangingCurrencyRef.current) {
          return
        }
        if (beneficiaryAmount < minBeneficiary) {
          setBeneficiaryValueError(true)
          // Get current errors and merge with new min amount error
          const currentErrors = form.getFieldError('beneficiary_value')
          const minAmountError = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
          // Merge errors - keep max limit errors, add min amount error
          const hasMinAmountError = currentErrors?.some((err: string) =>
            err?.includes('must be at least')
          )

          const otherErrors = currentErrors?.filter(
            (err: string) => !err?.includes('must be at least')
          )

          const allErrors = hasMinAmountError
            ? currentErrors
            : [...otherErrors, minAmountError]
          form.setFields([
            {
              name: 'beneficiary_value',
              errors: allErrors,
            },
          ])
        } else {
          setBeneficiaryValueError(false)
          // Only clear min amount errors, keep other validation errors (like max limit)
          const currentErrors = form.getFieldError('beneficiary_value')
          // Filter out min amount errors, keep other errors
          const otherErrors = currentErrors?.filter(
            (err: string) => !err?.includes('must be at least')
          )
          if (otherErrors.length === 0) {
            form.setFields([{ name: 'beneficiary_value', errors: [] }])
          } else {
            form.setFields([{ name: 'beneficiary_value', errors: otherErrors }])
          }
        }
      }
    }
  }, [
    paymentAmount,
    beneficiaryAmount,
    transferFee,
    serviceFee,
    checked,
    loadingFee,
    configFee,
    sendCurrency,
    exchangeRate,
    paymentDecimal,
    beneficiaryDecimal,
    hasChangingCurrencyRef.current,
  ])

  useEffect(() => {
    if (!dataAllCurrency || !sendCurrency) return
    if (editData && lastFilledEditIdRef.current === editData?.id && !hasSetDefaultTransferModeRef.current) {
      hasSetDefaultTransferModeRef.current = true
      return
    }

    // if (editData) {
    //   const isSameBeneficiaryAsOriginal =
    //     !!editData?.id &&
    //     !!editData?.beneficiary &&
    //     !!beneficiarySelected &&
    //     editData?.beneficiary?.id === beneficiarySelected?.id

    //   // Edit mode: keep transfer mode/payment method from existing order data
    //   // (logic at lines 845-860) until user actually changes beneficiary.
    //   if (isSameBeneficiaryAsOriginal) {
    //     return
    //   }
    // }

    const isCrypto = dataAllCurrency.some(
      (item: any) => item.id === sendCurrency && item.type === 'CRYPTO'
    )

    const isFiat = dataAllCurrency.some(
      (item: any) => item.id === sendCurrency && item.type === 'FIAT'
    )

    const walletItems = Array.isArray(dataListWallet?.items)
      ? dataListWallet.items
      : []

    const bankAccountItems = Array.isArray(dataListBankAccount?.items)
      ? dataListBankAccount.items
      : []

    if (isCrypto) {
      setMethod(listMethod.CRYPTO_CRYPTO)
      if (loadingListWallet) return

      let transferModeData = listTransferMode.CRYPTO_NETWORK
      if (
        walletItems.length <= 0 &&
        beneficiarySelected?.refUser?.smartUID
      ) {
        transferModeData = listTransferMode.INTERNAL_TRANSFER
      }
      setTransferMode(transferModeData)
    } else if (isFiat) {
      setMethod(listMethod.FIAT_FIAT)
      if (loadingListBankAccount) return

      let transferModeData = listTransferMode.BANK_TRANSFER
      if (
        bankAccountItems.length <= 0 &&
        beneficiarySelected?.refUser?.smartUID
      ) {
        transferModeData = listTransferMode.INTERNAL_TRANSFER
      }
      setTransferMode(transferModeData)
    }
  }, [
    sendCurrency,
    beneficiarySelected,
    dataAllCurrency,
    dataListWallet?.items,
    dataListBankAccount?.items,
    loadingListWallet,
    loadingListBankAccount,
    beneficiarySelected?.refUser?.smartUID,
    beneficiarySelected?.id,
    editData?.id,
    editData?.beneficiary?.id,
  ])

  useEffect(() => {
    if (
      Array.isArray(dataListCompany?.items) &&
      dataListCompany?.items.length > 0
    ) {
      if (form?.getFieldValue('beneficiary')) {
        const selected = dataListCompany?.items?.find(
          (item: any) => item.id === form?.getFieldValue('beneficiary')
        )
        setBeneficiarySelected(selected)
      }
      setListBeneficiary(dataListCompany?.items)
    }
  }, [dataListCompany])

  useEffect(() => {
    if (loadingListWallet) return

    if (
      Array.isArray(dataListWallet?.items) &&
      dataListWallet?.items.length > 0
    ) {
      setListWallet(dataListWallet?.items)
      if (form?.getFieldValue('beneficiary_wallet_address')) {
        const selected = dataListWallet?.items?.find(
          (item: any) =>
            item.id === form?.getFieldValue('beneficiary_wallet_address')
        )
        setWalletSelected(selected)
      }
    } else {
      setListWallet([])
    }
  }, [dataListWallet, loadingListWallet])

  useEffect(() => {
    if (loadingListBankAccount) return

    if (
      Array.isArray(dataListBankAccount?.items) &&
      dataListBankAccount?.items.length > 0
    ) {
      setListBankAccount(dataListBankAccount?.items)
      if (form?.getFieldValue('beneficiary_bank_account')) {
        const selected = dataListBankAccount?.items?.find(
          (item: any) =>
            item.id === form?.getFieldValue('beneficiary_bank_account')
        )
        setBankAccountSelected(selected)
      }
    } else {
      setListBankAccount([])
    }
  }, [dataListBankAccount, loadingListBankAccount])

  useEffect(() => {
    if (
      Array.isArray(dataListWalletProvider?.items) &&
      dataListWalletProvider?.items.length > 0
    )
      setListWalletProvider(dataListWalletProvider?.items)
    else setListWalletProvider([])
  }, [dataListWalletProvider])

  useEffect(() => {
    if (
      Array.isArray(dataListBankAccountProvider?.items) &&
      dataListBankAccountProvider?.items.length > 0
    )
      setListBankAccountProvider(dataListBankAccountProvider?.items)
    else setListBankAccountProvider([])
  }, [dataListBankAccountProvider])

  useEffect(() => {
    if (transferMode === listTransferMode.INTERNAL_TRANSFER)
      if (beneficiarySelected?.refUser?.smartUID) {
        form.setFieldValue('smartUID', beneficiarySelected?.refUser?.smartUID)
        fetchInfoByUserCode(beneficiarySelected?.refUser?.smartUID)
      } else {
        form.setFieldValue('smartUID', undefined)
        setInfoByUserCode(null)
      }
  }, [transferMode, beneficiarySelected])

  // Fee and rate are now polled together in the unified polling effect above

  // Re-validate beneficiary_value after fee is fetched to ensure validation uses correct fee data
  // This is specifically to handle the case when paymentCurrency changes and fees are recalculated
  // Note: We don't include sendCurrency in dependencies because handleChangeSendCurrency already has its own validation logic
  useEffect(() => {
    if (!configFee || loadingFee || isChangingCurrencyRef.current) {
      return
    }

    // If beneficiary pays fee, wait for transferFee and serviceFee to be calculated
    if (checked === listPayFee[1].value) {
      if (transferFee === null || serviceFee === null) {
        return
      }
    }

    const beneficiaryValue = form.getFieldValue('beneficiary_value')

    if (beneficiaryValue && Number(beneficiaryValue) > 0) {
      // Use setTimeout to ensure state is updated and avoid conflicts with other validations
      // Increased timeout to 200ms to ensure currency change operations have completed
      setTimeout(() => {
        // Double-check flag to avoid conflicts with currency change operations
        if (!isChangingCurrencyRef.current) {
          form
            .validateFields(['beneficiary_value'])
            .then(() => {
              // Clear preserved error since validation passed with new fees
              preservedBeneficiaryErrorRef.current = null
            })
            .catch(() => {
              // Error is expected if value is invalid, just ensure validation runs
              // Don't clear preserved error here - let the validator handle it
            })
        }
      }, 200)
    }
  }, [
    configFee,
    loadingFee,
    paymentCurrency,
    sendCurrency,
    transferFee,
    serviceFee,
    checked,
    form,
  ])

  // Rate polling is handled by the unified polling effect above

  useEffect(() => {
    if (!avlBalance) return
    const totalBalance = avlBalance?.data?.data?.totalBalance ?? 0
    if (totalBalance === 0) {
      setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
      setDisabledPortfolio(true)
      return
    }
    if (transferFee === null || serviceFee === null) return
    if (isFirstLoadBalance) {
      if (checked === listPayFee[0].value) {
        if (
          totalBalance >=
          Number(
            (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal)
          )
        ) {
          setPaymentMethod(listPaymentMethod.PORTFOLIO)
          setDisabledPortfolio(false)
        } else {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
          setDisabledPortfolio(true)
        }
      } else {
        if (totalBalance >= Number(paymentAmount.toFixed(paymentDecimal))) {
          setPaymentMethod(listPaymentMethod.PORTFOLIO)
          setDisabledPortfolio(false)
        } else {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
          setDisabledPortfolio(true)
        }
      }
    } else {
      if (checked === listPayFee[0].value) {
        if (
          totalBalance <
          Number(
            (paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal)
          )
        ) {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
          setDisabledPortfolio(true)
        } else {
          setDisabledPortfolio(false)
        }
      } else {
        if (totalBalance < Number(paymentAmount.toFixed(paymentDecimal))) {
          setPaymentMethod(listPaymentMethod.DIRECTER_TRANSFER)
          setDisabledPortfolio(true)
        } else {
          setDisabledPortfolio(false)
        }
      }
    }
  }, [
    checked,
    paymentAmount,
    transferFee,
    serviceFee,
    avlBalance,
    isFirstLoadBalance,
  ])

  useEffect(() => {
    if (!configFee || loadingFee) {
      return
    }
    if (checked === listPayFee[0].value) {
      const isCrypto = dataAllCurrency.some(
        (item: any) => item.id === sendCurrency && item.type === 'CRYPTO'
      )
      switch (configFee?.serviceFeeType) {
        case 'PERCENTAGE': {
          if (!beneficiaryAmount) setServiceFee(null)
          else
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) /
                  100 /
                  exchangeRate,
                  paymentDecimal
                )
              )
            )
          break
        }
        case 'FIXED':
          setServiceFee(
            Number(
              roundUp(
                (configFee?.serviceFee * configFee?.usdToFeeCurrencyRate) /
                exchangeRate,
                paymentDecimal
              )
            )
          )
          break
      }
      if (transferMode === listTransferMode.INTERNAL_TRANSFER) {
        setFeeConfigId(undefined)
        setTransferFee(0)
      } else
        switch (
        isCrypto
          ? configFee?.cryptoTransferFeeType
          : configFee?.fiatTransferFeeType
        ) {
          case 'PERCENTAGE': {
            if (!beneficiaryAmount) setTransferFee(null)
            else
              setTransferFee(
                Number(
                  roundUp(
                    (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                    100 /
                    exchangeRate,
                    paymentDecimal
                  )
                )
              )
            break
          }
          case 'FIXED':
            setTransferFee(
              Number(
                roundUp(
                  (configFee?.transferFee * configFee?.usdToFeeCurrencyRate) /
                  exchangeRate,
                  paymentDecimal
                )
              )
            )
            break
          case 'DYNAMIC': {
            // For dynamic crypto fees, determine the correct network:
            // 1) Prefer the currently selected wallet's network (create/edit after user changes wallet)
            // 2) On initial edit load, fall back to the order's beneficiary.networkId
            let networkIdForFee = walletSelected?.network?.id

            if (!networkIdForFee && editData?.id && !hasLoadedRateFeeForEditRef.current) {
              networkIdForFee =
                editData?.beneficiary?.networkId ??
                editData?.beneficiary?.network?.id
            }

            if (!networkIdForFee) {
              setFeeConfigId(null)
              setTransferFee(null)
            } else {
              const transferFeeConfig = configFee?.transferFees?.find(
                (item: any) => item.networkId === networkIdForFee
              )

              setFeeConfigId(transferFeeConfig?.id)

              if (!transferFeeConfig) {
                setTransferFee(null)
              } else {
                setTransferFee(
                  Number(
                    roundUp(
                      (transferFeeConfig?.fee || 0) / exchangeRate,
                      paymentDecimal
                    )
                  )
                )
              }

              if (editData?.id && !hasLoadedRateFeeForEditRef.current) {
                hasLoadedRateFeeForEditRef.current = true
              }
            }
            break
          }
        }
    } else {
      const isCrypto = dataAllCurrency.some(
        (item: any) => item.id === sendCurrency && item.type === 'CRYPTO'
      )
      switch (configFee?.serviceFeeType) {
        case 'PERCENTAGE': {
          if (!beneficiaryAmount) setServiceFee(null)
          else
            setServiceFee(
              Number(
                roundUp(
                  ((beneficiaryAmount || 0) * configFee?.serviceFee) / 100,
                  beneficiaryDecimal
                )
              )
            )
          break
        }
        case 'FIXED':
          setServiceFee(
            Number(
              roundUp(
                configFee?.serviceFee * configFee?.usdToFeeCurrencyRate,
                beneficiaryDecimal
              )
            )
          )
          break
      }
      if (transferMode === listTransferMode.INTERNAL_TRANSFER) {
        setFeeConfigId(undefined)
        setTransferFee(0)
      } else
        switch (
        isCrypto
          ? configFee?.cryptoTransferFeeType
          : configFee?.fiatTransferFeeType
        ) {
          case 'PERCENTAGE': {
            if (!beneficiaryAmount) setTransferFee(null)
            else
              setTransferFee(
                Number(
                  roundUp(
                    (Number(beneficiaryAmount || 0) * configFee?.transferFee) /
                    100,
                    beneficiaryDecimal
                  )
                )
              )
            break
          }
          case 'FIXED':
            setTransferFee(
              Number(
                roundUp(
                  configFee?.transferFee * configFee?.usdToFeeCurrencyRate,
                  beneficiaryDecimal
                )
              )
            )
            break
          case 'DYNAMIC': {
            // Same logic as above branch but without applying exchangeRate
            let networkIdForFee = walletSelected?.network?.id

            if (!networkIdForFee && editData?.id && !hasLoadedRateFeeForEditRef.current) {
              networkIdForFee =
                editData?.beneficiary?.networkId ??
                editData?.beneficiary?.network?.id
            }

            if (!networkIdForFee) {
              setFeeConfigId(null)
              setTransferFee(null)
            } else {
              const transferFeeConfig = configFee?.transferFees?.find(
                (item: any) => item.networkId === networkIdForFee
              )

              setFeeConfigId(transferFeeConfig?.id)

              if (!transferFeeConfig) {
                setTransferFee(null)
              } else {
                setTransferFee(
                  Number(
                    roundUp(
                      transferFeeConfig?.fee || 0,
                      beneficiaryDecimal
                    )
                  )
                )
              }

              if (editData?.id && !hasLoadedRateFeeForEditRef.current) {
                hasLoadedRateFeeForEditRef.current = true
              }
            }
            break
          }
        }
    }
  }, [
    configFee,
    checked,
    paymentAmount,
    beneficiaryAmount,
    walletSelected,
    transferMode,
    exchangeRate,
    loadingFee,
    sendCurrency,
    dataAllCurrency,
    paymentDecimal,
    beneficiaryDecimal,
  ])

  useEffect(() => {
    let beneficiaryRequiredFiels = [...requiredFields]
    if (transferMode === listTransferMode.CRYPTO_NETWORK)
      beneficiaryRequiredFiels = [
        ...requiredFields,
        'beneficiary_wallet_address',
      ]
    else if (transferMode === listTransferMode.BANK_TRANSFER)
      beneficiaryRequiredFiels = [...requiredFields, 'beneficiary_bank_account']
    else beneficiaryRequiredFiels = [...requiredFields, 'smartUID']

    let newRequiredFiels = [...beneficiaryRequiredFiels]
    if (paymentMethod === listPaymentMethod.DIRECTER_TRANSFER) {
      const type = (
        dataAllCurrency.find((item: any) => item.id === paymentCurrency) as any
      )?.type

      if (type === 'FIAT')
        newRequiredFiels = [
          ...beneficiaryRequiredFiels,
          'companies_default',
          'providerBankAccount',
        ]
      else
        newRequiredFiels = [
          ...beneficiaryRequiredFiels,
          'companies_default',
          'providerWallet',
        ]
    }
    const values = form.getFieldsValue(newRequiredFiels)
    const allFilled = newRequiredFiels.every((field) => !!values[field])
    setIsAllFieldsFilled(allFilled)
  }, [
    beneficiary_value,
    payment_value,
    beneficiary,
    send_date,
    purposeOfRemittance,
    transferMode,
    paymentMethod,
    paymentCurrency,
    beneficiary_bank_account,
    beneficiary_wallet_address,
    smartUID,
    providerBankAccount,
    providerWallet,
  ])

  useEffect(() => {
    if (
      loadingCompanyCurrency ||
      companyDefaultCurrency === null ||
      paymentMethod === listPaymentMethod.PORTFOLIO
    )
      return
    if (companyDefaultCurrency?.length) {
      form.setFields([{ name: 'payment_currency', errors: [] }])
      setDisabledProvider(false)
    } else {
      form.setFields([
        {
          name: 'payment_currency',
          errors: ['No provider supports this currency'],
        },
      ])
      setDisabledProvider(true)
    }
  }, [
    companyDefaultCurrency,
    loadingCompanyCurrency,
    paymentMethod,
    paymentCurrency,
    form,
  ])

  const handleChangeSendCurrency = async (value: number) => {
    // Mark that user has changed currency, no longer default
    isSettingDefaultRef.current = false
    // Mark that we're changing currency to prevent useEffect from recalculating
    isChangingCurrencyRef.current = true
    hasChangingCurrencyRef.current = true

    // Save current values BEFORE changing currency
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    let beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    const currency = dataAllCurrency?.find(
      (item: any) => item?.id === value
    ) as any
    const newBeneficiaryDecimal = currency?.decimals

    // Check if new currency is same as payment currency
    const isSameAsPaymentCurrency = value === paymentCurrency

    // IMPORTANT: Set sendCurrency FIRST to trigger rate refetch
    // Clear configFee immediately when changing currency to prevent validation with old fee data
    setConfigFee(null)
    setTransferFee(null)
    setServiceFee(null)
    setSendCurrency(value)
    setBeneficiaryDecimal(newBeneficiaryDecimal)
    setSendCurrencySymbol(currency?.symbol)

    // Determine which field to keep:
    // 1. If user has edited a field, keep that edited field
    // 2. If user hasn't edited, always keep beneficiary_value as default
    const hasEditedBeneficiary =
      hasUserEditedInputRef.current &&
      lastChange === 'BENEFICARY' &&
      beneficiaryValueNum > 0

    const hasEditedPayment =
      hasUserEditedInputRef.current &&
      lastChange === 'SENDER' &&
      paymentValueNum > 0

    // If user hasn't edited, always default to keeping beneficiary_value
    if (!hasUserEditedInputRef.current) {
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)
      // If user hasn't edited, always use beneficiary_value
      // Keep the current value if it exists, don't set default
      if (!currentBeneficiaryValue || beneficiaryValueNum === 0) {
        // If beneficiary_value is empty/0, there's no value to keep, will handle in else block at the end
        beneficiaryValueNum = 0
      }
    }

    // Determine which field to keep
    // If user hasn't edited, always keep beneficiary_value (if it has a value)
    const keepBeneficiary =
      hasEditedBeneficiary ||
      (!hasUserEditedInputRef.current && beneficiaryValueNum > 0)
    const keepPayment = hasEditedPayment && !keepBeneficiary

    // Wait for new exchange rate to be fetched if currencies are different
    let newExchangeRate = exchangeRate
    if (!isSameAsPaymentCurrency) {
      // Fetch rate directly with new currency parameters
      try {
        // Add timestamp to force fresh fetch (cache busting)
        const rateResult = await getExchangeRate({
          from: paymentCurrency,
          to: value,
        })

        // rateResult.data has the same structure as rate: { data: { data: ... } }
        // Try different possible response structures
        const fetchedRate =
          rateResult?.data?.data?.data ||
          rateResult?.data?.data ||
          rateResult?.data ||
          exchangeRate

        // Only use fetched rate if it's valid and different from old rate
        if (fetchedRate && fetchedRate > 0 && fetchedRate !== exchangeRate) {
          newExchangeRate = fetchedRate
          setExchangeRate(newExchangeRate)
        } else if (fetchedRate && fetchedRate > 0) {
          // Rate is valid but same as old (might be correct if currencies haven't changed)
          newExchangeRate = fetchedRate
        }
      } catch (error) {
        // Use old rate if fetch fails
        newExchangeRate = exchangeRate
      }
    } else {
      // Same currency, rate is 1
      newExchangeRate = 1
      if (exchangeRate !== 1) {
        setExchangeRate(1)
      }
    }

    // If keeping beneficiary_value (user edited beneficiary_value or it's the default field for this select)
    // Keep beneficiary_value and recalculate payment_value from it
    if (keepBeneficiary) {
      // Keep the beneficiary_value (either edited by user or default for this select)
      const beneValue = formatNumberV2(beneficiaryValueNum, newBeneficiaryDecimal)

      // Set beneficiary_value first (keep the value)
      form.setFieldsValue({
        beneficiary_value: beneValue,
      })
      setBeneficiaryAmount(beneficiaryValueNum)
      setDirection(Direction.BEN_TO_PAY)
      setLastChange('BENEFICARY')

      // Calculate payment_value from beneficiary_value with new exchange rate
      if (!isSameAsPaymentCurrency) {
        // Calculate payment_value from beneficiary_value with new exchange rate
        const newPaymentValueNum =
          beneficiaryValueNum * (1 / newExchangeRate)
        const newPaymentValue = formatNumberV2(newPaymentValueNum, paymentDecimal)

        // Set payment_value from beneficiary_value with new exchange rate
        form.setFieldsValue({
          payment_value: newPaymentValue,
        })
        setPaymentAmount(newPaymentValueNum)
      } else {
        // Currencies are the same, payment_value = beneficiary_value
        const payValue = formatNumberV2(beneficiaryValueNum, paymentDecimal)
        form.setFieldsValue({
          payment_value: payValue,
        })
        setPaymentAmount(beneficiaryValueNum)
      }

      // Validate beneficiary_value first (since we're keeping it)
      try {
        await form.validateFields(['beneficiary_value'])
        // If beneficiary_value is valid, validate payment_value after calculating
        setTimeout(async () => {
          isChangingCurrencyRef.current = true
          try {
            await form.validateFields(['payment_value'])
            isChangingCurrencyRef.current = false
          } catch (error) {
            const paymentErrors = form.getFieldError('payment_value')

            setTimeout(() => {
              isChangingCurrencyRef.current = false
            }, 200)
          }
        }, 150)
      } catch (error) {
        // If beneficiary_value has error, still calculate and validate payment_value to show its errors
        setTimeout(async () => {
          isChangingCurrencyRef.current = true
          try {
            await form.validateFields(['payment_value'])
            isChangingCurrencyRef.current = false
          } catch (error) {
            const paymentErrors = form.getFieldError('payment_value')
            setTimeout(() => {
              isChangingCurrencyRef.current = false
            }, 200)
          }
        }, 150)
      }
    }
    // If keeping payment_value (user edited payment_value most recently)
    else if (keepPayment) {
      const paymentValue = formatNumberV2(paymentValueNum, paymentDecimal)

      // Set payment_value first
      form.setFieldsValue({
        payment_value: paymentValue,
      })
      setPaymentAmount(paymentValueNum)
      setDirection(Direction.PAY_TO_BEN)
      setLastChange('SENDER')

      // If currencies are the same, check max limit before setting beneficiary_value = payment_value
      if (isSameAsPaymentCurrency) {
        // Check max limit for beneficiary currency
        const maxLimitBeneficiary = getMaxLimit(value)

        // Set beneficiary_value = payment_value (even if exceeds limit, validation will show error)
        // Format beneficiary_value with newBeneficiaryDecimal to preserve format
        const beneficiaryValueFormatted = formatNumberV2(
          paymentValueNum,
          newBeneficiaryDecimal
        )
        form.setFieldsValue({
          beneficiary_value: beneficiaryValueFormatted,
        })
        setBeneficiaryAmount(paymentValueNum)
      }

      // Calculate and set beneficiary_value from payment_value (if currencies are different)
      // Always recalculate beneficiary_value even if payment_value validation fails
      if (!isSameAsPaymentCurrency) {
        // Re-read payment_value from form to ensure we have the latest value
        const currentPaymentValueAfterChange =
          form.getFieldValue('payment_value')

        const paymentValueNumAfterChange =
          Number(parseFormattedNumberInput(currentPaymentValueAfterChange)) || paymentValueNum

        const newBeneficiaryValueNum = paymentValueNumAfterChange * newExchangeRate
        const newBeneficiaryValue = formatNumberV2(newBeneficiaryValueNum, newBeneficiaryDecimal)

        // Always set beneficiary_value from payment_value with new exchange rate
        form.setFieldsValue({
          beneficiary_value: newBeneficiaryValue,
        })
        setBeneficiaryAmount(newBeneficiaryValueNum)

        // Validate payment_value first
        try {
          await form.validateFields(['payment_value'])
          // If payment_value is valid, validate beneficiary_value after setting
          // Use a longer timeout to ensure form state is updated
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['beneficiary_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If beneficiary_value has error, ensure it's displayed
              // Get the error message
              const beneficiaryErrors = form.getFieldError('beneficiary_value')

              // If error exists but not displayed, set it explicitly
              if (beneficiaryErrors.length > 0) {
                // Error is already set, just ensure it's not cleared
                // Keep flag true a bit longer to prevent onValuesChange from clearing
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              } else {
                // Force validation again to set error
                form.validateFields(['beneficiary_value']).catch(() => {
                  // Error is expected and will be displayed
                  setTimeout(() => {
                    isChangingCurrencyRef.current = false
                  }, 200)
                })
              }
            }
          }, 150)
        } catch (error) {
          // If payment_value has error, still validate beneficiary_value to show its errors
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['beneficiary_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If beneficiary_value has error, ensure it's displayed
              const beneficiaryErrors = form.getFieldError('beneficiary_value')

              // Keep flag true a bit longer to prevent onValuesChange from clearing
              setTimeout(() => {
                isChangingCurrencyRef.current = false
              }, 200)
            }
          }, 150)
        }
      } else {
        // Currencies are the same, beneficiary_value already set equal to payment_value
        // Validate payment_value first
        try {
          await form.validateFields(['payment_value'])
          // If payment_value is valid, validate beneficiary_value
          setTimeout(() => {
            form.validateFields(['beneficiary_value']).catch(() => {
              // Error is expected if payment_value has error
            })
          }, 0)
        } catch (error) {
          // If payment_value has error, beneficiary_value should still equal payment_value
          // Validate beneficiary_value to show error if needed
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['beneficiary_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If beneficiary_value has error, ensure it's displayed
              const beneficiaryErrors = form.getFieldError('beneficiary_value')

              // Keep flag true a bit longer to prevent onValuesChange from clearing
              setTimeout(() => {
                isChangingCurrencyRef.current = false
              }, 200)
            }
          }, 100)
        }
      }
    }
    // If no value exists or both are 0, set default value
    else {
      const beneValue = 0
      const payValueNum = beneValue * (1 / exchangeRate)
      const payValue = formatNumberV2(payValueNum, paymentDecimal)
      form.setFieldsValue({
        beneficiary_value: formatNumberV2(beneValue, newBeneficiaryDecimal),
        payment_value: payValue,
      })
      setBeneficiaryAmount(0)
      setPaymentAmount(payValueNum)
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)
    }
    // Reset related selections
    setBeneficiarySelected(null)
    setWalletSelected(null)
    setBankAccountSelected(null)
    setInfoByUserCode(null)
    setCompanyDefaultSelected(null)
    setWalletSelectedProvider(null)
    setBankAccountSelectedProvider(null)
    form?.resetFields([
      'beneficiary',
      'beneficiary_wallet_address',
      'beneficiary_bank_account',
      'smartUID',
      'companies_default',
      'providerBankAccount',
      'providerWallet',
      'supportDocument',
    ])
    setDocument({ file: {} as UploadFile<any>, fileList: [] })

    // Validate field that was kept after currency change
    setTimeout(async () => {
      // Validate the field that was kept first
      if (keepBeneficiary) {
        try {
          await form.validateFields(['beneficiary_value'])
          // If beneficiary_value is valid, validate payment_value
          try {
            await form.validateFields(['payment_value'])
          } catch (paymentError) {
            const paymentErrors = form.getFieldError('payment_value')
            // Payment value has errors, ensure they are displayed
          }
        } catch (beneficiaryError) {
          const beneficiaryErrors = form.getFieldError('beneficiary_value')
          // If beneficiary_value has errors, payment_value is already cleared, no need to validate it
        }
      } else if (keepPayment) {
        try {
          await form.validateFields(['payment_value'])
          // If payment_value is valid, validate beneficiary_value
          try {
            await form.validateFields(['beneficiary_value'])
          } catch (beneficiaryError) {
            const beneficiaryErrors = form.getFieldError('beneficiary_value')
            // Beneficiary value has errors, ensure they are displayed
          }
        } catch (paymentError) {
          const paymentErrors = form.getFieldError('payment_value')
          // If payment_value has errors, beneficiary_value is already cleared, no need to validate it
        }
      }

      // Reset flag after validation completes, but ensure errors persist
      setTimeout(() => {
        isChangingCurrencyRef.current = false

        // Re-validate to ensure errors are still displayed if values are still invalid
        const currentPaymentValue = form.getFieldValue('payment_value')
        const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
        if (currentPaymentValue) {
          form.validateFields(['payment_value'])
        }
        if (currentBeneficiaryValue) {
          form.validateFields(['beneficiary_value'])
        }
      }, 50)
    }, 100)
  }

  const handleChangePaymentCurrency = async (value: number) => {
    // Mark that user has changed currency, no longer default
    isSettingDefaultRef.current = false
    // Mark that we're changing currency to prevent useEffect from recalculating
    isChangingCurrencyRef.current = true
    hasChangingCurrencyRef.current = true

    // IMPORTANT: Preserve beneficiary_value error BEFORE clearing configFee
    // This ensures the error is not lost during currency change
    const beneficiaryErrors = form.getFieldError('beneficiary_value')
    const hasMinAmountError = beneficiaryErrors.some((err: string) =>
      err?.includes('must be at least')
    )
    if (hasMinAmountError) {
      preservedBeneficiaryErrorRef.current = beneficiaryErrors[0] || null
    } else {
      preservedBeneficiaryErrorRef.current = null
    }

    // Save current values
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    let beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    const currency = dataAllCurrency?.find(
      (item: any) => item?.id === value
    ) as any
    const newPaymentDecimal = currency?.decimals

    // Check if new currency is same as beneficiary currency
    const isSameAsBeneficiaryCurrency = value === sendCurrency

    // IMPORTANT: Set paymentCurrency FIRST to trigger rate refetch
    // Clear configFee immediately when changing currency to prevent validation with old fee data
    setConfigFee(null)
    setTransferFee(null)
    setServiceFee(null)
    setPaymentCurrency(value)
    setPaymentDecimal(newPaymentDecimal)
    setPaymentCurrencySymbol(currency?.symbol)
    setIsFirstLoadBalance(true)

    // Determine which field to keep:
    // 1. If user has edited a field, keep that edited field
    // 2. If user hasn't edited, always keep beneficiary_value as default
    const hasEditedBeneficiary =
      hasUserEditedInputRef.current &&
      lastChange === 'BENEFICARY' &&
      beneficiaryValueNum > 0

    const hasEditedPayment =
      hasUserEditedInputRef.current &&
      lastChange === 'SENDER' &&
      paymentValueNum > 0

    // If user hasn't edited, always default to keeping beneficiary_value
    if (!hasUserEditedInputRef.current) {
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)
      // If user hasn't edited, always use beneficiary_value
      // Keep the current value if it exists, don't set default
      if (!currentBeneficiaryValue || beneficiaryValueNum === 0) {
        // If beneficiary_value is empty/0, there's no value to keep, will handle in else block at the end
        beneficiaryValueNum = 0
      }
    }

    // Determine which field to keep
    // If user hasn't edited, always keep beneficiary_value (if it has a value)
    const keepBeneficiary =
      hasEditedBeneficiary ||
      (!hasUserEditedInputRef.current && beneficiaryValueNum > 0)
    const keepPayment = hasEditedPayment && !keepBeneficiary

    // Wait for new exchange rate to be fetched if currencies are different
    let newExchangeRate = exchangeRate
    if (!isSameAsBeneficiaryCurrency) {
      // Fetch rate directly with new currency parameters
      try {
        const rateResult = await getExchangeRate({
          from: value,
          to: sendCurrency,
        })

        const fetchedRate =
          rateResult?.data?.data?.data ||
          rateResult?.data?.data ||
          rateResult?.data ||
          exchangeRate

        if (fetchedRate && fetchedRate > 0 && fetchedRate !== exchangeRate) {
          newExchangeRate = fetchedRate
          setExchangeRate(newExchangeRate)
        } else if (fetchedRate && fetchedRate > 0) {
          newExchangeRate = fetchedRate
        }
      } catch (error) {
        newExchangeRate = exchangeRate
      }
    } else {
      // Same currency, rate is 1
      newExchangeRate = 1
      if (exchangeRate !== 1) {
        setExchangeRate(1)
      }
    }

    // If keeping beneficiary_value (user edited beneficiary_value most recently)
    if (keepBeneficiary) {
      const beneValue = formatNumberV2(beneficiaryValueNum, beneficiaryDecimal)

      // Set beneficiary_value first
      form.setFieldsValue({
        beneficiary_value: beneValue,
      })
      setBeneficiaryAmount(beneficiaryValueNum)
      setDirection(Direction.BEN_TO_PAY)
      setLastChange('BENEFICARY')

      // If currencies are the same, check max limit before setting payment_value = beneficiary_value
      if (isSameAsBeneficiaryCurrency) {
        // Check max limit for payment currency
        const maxLimitPayment = getMaxLimit(value)

        // Set payment_value = beneficiary_value (even if exceeds limit, validation will show error)
        // Format payment_value with newPaymentDecimal to preserve format
        const paymentValueFormatted = formatNumberV2(
          beneficiaryValueNum,
          newPaymentDecimal
        )

        form.setFieldsValue({
          payment_value: paymentValueFormatted,
        })
        setPaymentAmount(beneficiaryValueNum)

        // If exceeds max limit, validate to show error immediately
        if (maxLimitPayment !== null && beneficiaryValueNum > maxLimitPayment) {
          // Error will be shown by validation below
        }
      }

      // Validate beneficiary_value first
      try {
        await form.validateFields(['beneficiary_value'])
        // If beneficiary_value is valid, calculate and set payment_value (if currencies are different)
        if (!isSameAsBeneficiaryCurrency) {
          const rateToUse = newExchangeRate
          const newPaymentValueNum =
            beneficiaryValueNum * (1 / rateToUse)

          const newPaymentValue = formatNumberV2(
            newPaymentValueNum,
            newPaymentDecimal
          )

          form.setFieldsValue({
            payment_value: newPaymentValue,
          })
          setPaymentAmount(newPaymentValueNum)

          // Validate payment_value after setting
          setTimeout(() => {
            form.validateFields(['payment_value']).catch(() => {
              // Error is expected if payment_value has error
            })
          }, 0)
        } else {
          // Currencies are the same, payment_value already set, just validate it
          setTimeout(() => {
            form.validateFields(['payment_value']).catch(() => {
              // Error is expected if payment_value has error
            })
          }, 0)
        }
      } catch (error) {
        // If beneficiary_value has error, clear validation of payment_value
        if (!isSameAsBeneficiaryCurrency) {
          form.resetFields(['payment_value'])
          form.setFields([{ name: 'payment_value', errors: [] }])
          setPaymentAmount(0)
        } else {
          // payment_value already equals beneficiary_value, clear its validation
          form.setFields([{ name: 'payment_value', errors: [] }])
          setTimeout(async () => {
            // Keep flag true to prevent onValuesChange from clearing error
            isChangingCurrencyRef.current = true
            try {
              await form.validateFields(['payment_value'])
              isChangingCurrencyRef.current = false
            } catch (error) {
              // If payment_value has error, ensure it's displayed
              const paymentErrors = form.getFieldError('payment_value')

              // Keep flag true a bit longer to prevent onValuesChange from clearing
              setTimeout(() => {
                isChangingCurrencyRef.current = false
              }, 200)
            }
          }, 100)
        }
      }
    }
    // If keeping payment_value (user edited payment_value most recently)
    else if (keepPayment) {
      const paymentValue = formatNumberV2(paymentValueNum, newPaymentDecimal)

      // Set payment_value first
      form.setFieldsValue({
        payment_value: paymentValue,
      })
      setPaymentAmount(paymentValueNum)
      setDirection(Direction.PAY_TO_BEN)
      setLastChange('SENDER')

      // If currencies are the same, check max limit before setting beneficiary_value = payment_value
      if (isSameAsBeneficiaryCurrency) {
        // Check max limit for beneficiary currency
        const maxLimitBeneficiary = getMaxLimit(sendCurrency)

        // Set beneficiary_value = payment_value (even if exceeds limit, validation will show error)
        // Format beneficiary_value with beneficiaryDecimal to preserve format
        const beneficiaryValueFormatted =
          formatNumberV2(paymentValueNum, beneficiaryDecimal)
        form.setFieldsValue({
          beneficiary_value: beneficiaryValueFormatted,
        })
        setBeneficiaryAmount(paymentValueNum)

        // If exceeds max limit, validate to show error immediately
        if (
          maxLimitBeneficiary !== null &&
          paymentValueNum > maxLimitBeneficiary
        ) {
          // Error will be shown by validation below
        }
      }

      // Validate payment_value first
      try {
        await form.validateFields(['payment_value'])
        // If payment_value is valid, calculate and set beneficiary_value (if currencies are different)
        if (!isSameAsBeneficiaryCurrency) {
          const rateToUse = newExchangeRate
          const newBeneficiaryValueNum = paymentValueNum * rateToUse
          const newBeneficiaryValue = formatNumberV2(
            newBeneficiaryValueNum,
            beneficiaryDecimal
          )

          form.setFieldsValue({
            beneficiary_value: newBeneficiaryValue,
          })
          setBeneficiaryAmount(newBeneficiaryValueNum)
        } else {
          // Currencies are the same, beneficiary_value already set, validate it
        }

        // Validate beneficiary_value after setting to show error if invalid
        // Use a longer timeout to ensure form state is updated
        setTimeout(async () => {
          // Keep flag true to prevent onValuesChange from clearing error
          isChangingCurrencyRef.current = true
          try {
            await form.validateFields(['beneficiary_value'])
            isChangingCurrencyRef.current = false
          } catch (error) {
            // If beneficiary_value has error, ensure it's displayed
            // Get the error message
            const beneficiaryErrors = form.getFieldError('beneficiary_value')

            // If error exists but not displayed, set it explicitly
            if (beneficiaryErrors.length > 0) {
              // Error is already set, just ensure it's not cleared
              // Keep flag true a bit longer to prevent onValuesChange from clearing
              setTimeout(() => {
                isChangingCurrencyRef.current = false
              }, 200)
            } else {
              // Force validation again to set error
              form.validateFields(['beneficiary_value']).catch(() => {
                // Error is expected and will be displayed
                setTimeout(() => {
                  isChangingCurrencyRef.current = false
                }, 200)
              })
            }
          }
        }, 150)
      } catch (error) {
        // If payment_value has error, clear validation of beneficiary_value
        if (!isSameAsBeneficiaryCurrency) {
          form.resetFields(['beneficiary_value'])
          form.setFields([{ name: 'beneficiary_value', errors: [] }])
          setBeneficiaryAmount(0)
        } else {
          // beneficiary_value already equals payment_value, clear its validation
          form.setFields([{ name: 'beneficiary_value', errors: [] }])
        }
      }
    }
    // If no value exists or both are 0, set default value for beneficiary_value
    else {
      const beneValue = 0
      form.setFieldsValue({
        beneficiary_value: formatNumberV2(beneValue, beneficiaryDecimal),
      })
      setBeneficiaryAmount(beneValue)
      setLastChange('BENEFICARY')
      setDirection(Direction.BEN_TO_PAY)

      // Calculate payment_value from beneficiary_value
      if (!isSameAsBeneficiaryCurrency) {
        const payValueNum = beneValue * (1 / newExchangeRate)
        const payValue = formatNumberV2(payValueNum, newPaymentDecimal)
        form.setFieldsValue({ payment_value: payValue })
        setPaymentAmount(payValueNum)
      } else {
        const payValue = formatNumberV2(beneValue, newPaymentDecimal)
        form.setFieldsValue({ payment_value: payValue })
        setPaymentAmount(beneValue)
      }
    }
    setBeneficiarySelected(null)
    setWalletSelected(null)
    setBankAccountSelected(null)
    setInfoByUserCode(null)
    setCompanyDefaultSelected(null)
    setWalletSelectedProvider(null)
    setBankAccountSelectedProvider(null)
    form.resetFields([
      'beneficiary',
      'beneficiary_wallet_address',
      'beneficiary_bank_account',
      'smartUID',
      'companies_default',
      'providerBankAccount',
      'providerWallet',
      'supportDocument',
    ])
    setDocument({ file: {} as UploadFile<any>, fileList: [] })

    // Validate field that was kept after currency change
    setTimeout(async () => {
      // Validate the field that was kept first
      if (keepBeneficiary) {
        try {
          await form.validateFields(['beneficiary_value'])
          // If beneficiary_value is valid, validate payment_value
          try {
            await form.validateFields(['payment_value'])
          } catch (paymentError) {
            const paymentErrors = form.getFieldError('payment_value')
            // Payment value has errors, ensure they are displayed
          }
        } catch (beneficiaryError) {
          const beneficiaryErrors = form.getFieldError('beneficiary_value')
          // If beneficiary_value has errors, payment_value is already cleared, no need to validate it
        }
      } else if (keepPayment) {
        try {
          await form.validateFields(['payment_value'])
          // If payment_value is valid, validate beneficiary_value
          try {
            await form.validateFields(['beneficiary_value'])
          } catch (beneficiaryError) {
            const beneficiaryErrors = form.getFieldError('beneficiary_value')
            // Beneficiary value has errors, ensure they are displayed
          }
        } catch (paymentError) {
          const paymentErrors = form.getFieldError('payment_value')
          // If payment_value has errors, beneficiary_value is already cleared, no need to validate it
        }
      }

      // Reset flag after validation completes, but ensure errors persist
      setTimeout(() => {
        isChangingCurrencyRef.current = false

        // IMPORTANT: Fetch configFee again if it was cleared during currency change
        // configFee depends on sendCurrency, not paymentCurrency, so it should still be valid
        // But we cleared it to prevent validation with old data, so we need to fetch it again
        if (sendCurrency) {
          fetchFee(sendCurrency)
        }

        // Re-validate to ensure errors are still displayed if values are still invalid
        const currentPaymentValue = form.getFieldValue('payment_value')
        const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
        if (currentPaymentValue) {
          form.validateFields(['payment_value'])
        }
        if (currentBeneficiaryValue) {
          // Wait for transferFee and serviceFee to be recalculated with new paymentCurrency
          // This ensures validation runs with the correct fee data
          // Use a longer timeout to ensure all fee calculations are complete
          const validateBeneficiary = () => {
            // Check if fees are ready (for beneficiary pay fee case)
            const needsFees = checked === listPayFee[1].value
            const feesReady =
              !needsFees || (transferFee !== null && serviceFee !== null)

            if (feesReady && configFee && !loadingFee) {
              form
                .validateFields(['beneficiary_value'])
                .then(() => {
                  // Clear preserved error if validation passes
                  preservedBeneficiaryErrorRef.current = null
                })
                .catch(() => {
                  // Error is expected if value is invalid
                })
              return true
            } else {
              return false
            }
          }

          // Try validation after a delay
          setTimeout(() => {
            if (!validateBeneficiary()) {
              // Retry after a bit more time if fees are not ready
              setTimeout(() => {
                if (!validateBeneficiary()) {
                  // Final retry after even more time
                  setTimeout(() => {
                    validateBeneficiary()
                  }, 300)
                }
              }, 300)
            }
          }, 200)
        }
      }, 50)
    }, 100)
  }

  const getMaxLimit = (currency: number | undefined) => {
    const currencyETH = findCurrencyBySymbol(dataAllCurrency, 'ETH')
    const currencyUSD = findCurrencyBySymbol(dataAllCurrency, 'USD')
    const currencyUSDT = findCurrencyBySymbol(dataAllCurrency, 'USDT')
    const currencyDAI = findCurrencyBySymbol(dataAllCurrency, 'DAI')
    const currencyEUR = findCurrencyBySymbol(dataAllCurrency, 'EUR')
    const currencyHKD = findCurrencyBySymbol(dataAllCurrency, 'HKD')
    const currencyAUD = findCurrencyBySymbol(dataAllCurrency, 'AUD')
    const currencyJPY = findCurrencyBySymbol(dataAllCurrency, 'JPY')
    const currencyCHF = findCurrencyBySymbol(dataAllCurrency, 'CHF')
    const currencyCNY = findCurrencyBySymbol(dataAllCurrency, 'CNY')
    const currencyAED = findCurrencyBySymbol(dataAllCurrency, 'AED')
    const currencySGD = findCurrencyBySymbol(dataAllCurrency, 'SGD')
    const currencyGBP = findCurrencyBySymbol(dataAllCurrency, 'GBP')

    switch (currency) {
      case currencyETH?.id:
        return 10000000
      case currencyUSDT?.id:
      case currencyDAI?.id:
      case currencyUSD?.id:
      case currencyEUR?.id:
        return 10000000000
      case currencyHKD?.id:
        return 77800000000
      case currencyAUD?.id:
        return 15000000000
      case currencyJPY?.id:
        return 15479000000
      case currencyCHF?.id:
        return 8000000000
      case currencyCNY?.id:
        return 707000000000
      case currencyAED?.id:
        return 36700000000
      case currencySGD?.id:
        return 12900000000
      case currencyGBP?.id:
        return 7400000000
      default:
        return null
    }
  }

  const handleChangeBeneficiaryValue = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false
    hasUserEditedInputRef.current = true
    // Mark that user has input into beneficiary_value field
    hasInputBeneficiaryRef.current = true
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false
    // Clear preserved error when user manually enters a new value
    // This ensures validation runs with the new value instead of using preserved error
    preservedBeneficiaryErrorRef.current = null
    setLastChange('BENEFICARY')
    setDirection(Direction.BEN_TO_PAY)
    form.setFieldValue(
      'beneficiary_value',
      formatNumberOnChangeInput(e.target.value, beneficiaryDecimal)
    )
  }

  const handleChangePaymentValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Mark that user has manually entered, no longer default
    isSettingDefaultRef.current = false
    hasUserEditedInputRef.current = true
    // Mark that user has input into payment_value field
    hasInputPaymentRef.current = true
    // Reset currency change flag when user manually changes value
    isChangingCurrencyRef.current = false
    setLastChange('SENDER')
    setDirection(Direction.PAY_TO_BEN)
    form.setFieldValue(
      'payment_value',
      formatNumberOnChangeInput(e.target.value, paymentDecimal)
    )
  }

  const handleBlurSend = async () => {
    const rawValue = form.getFieldValue('beneficiary_value')
    const val = Number(parseFormattedNumberInput(rawValue))

    hasInputBeneficiaryRef.current = true
    preservedBeneficiaryErrorRef.current = null

    if (val) {
      setBeneficiaryAmount(val)
      form.setFieldsValue({
        beneficiary_value: formatNumberV2(val, beneficiaryDecimal),
      })
      setDirection(Direction.BEN_TO_PAY)
      setLastChange('BENEFICARY')

      // Always validate beneficiary_value on blur to ensure errors are shown
      // Validate immediately and then retry after delays to catch configFee state updates
      const performValidation = async () => {
        try {
          await form.validateFields(['beneficiary_value'])

          // If beneficiary_value is valid, calculate payment_value
          const calculatedPaymentValueNum = val * (1 / exchangeRate)

          // Check max limit for payment currency
          const maxLimitPayment = getMaxLimit(paymentCurrency)

          if (
            maxLimitPayment !== null &&
            calculatedPaymentValueNum > maxLimitPayment
          ) {
            form.setFieldsValue({
              payment_value: formatNumberV2(calculatedPaymentValueNum, paymentDecimal),
            })
            setPaymentAmount(calculatedPaymentValueNum)
            // Validate to show error
            setTimeout(() => {
              form.validateFields(['payment_value'])
            }, 0)
          } else {
            form.setFieldsValue({
              payment_value: formatNumberV2(calculatedPaymentValueNum, paymentDecimal),
            })
            setPaymentAmount(calculatedPaymentValueNum)
            // Validate payment_value
            setTimeout(() => {
              form.validateFields(['payment_value'])
            }, 0)
          }
        } catch (error) {
          // If beneficiary_value has error, don't calculate payment_value
          const beneficiaryErrors = form.getFieldError('beneficiary_value')
          form.resetFields(['payment_value'])
          setPaymentAmount(0)
        }
      }

      // Validate immediately
      setTimeout(() => {
        performValidation()
      }, 0)

      // Validate again after delays to catch configFee state updates
      setTimeout(() => {
        performValidation()
      }, 200)

      setTimeout(() => {
        performValidation()
      }, 500)
    } else {
      form.resetFields(['payment_value'])
      setPaymentAmount(0)
      setBeneficiaryAmount(0)
    }
  }

  const handleBlurPayment = async () => {
    const rawValue = form.getFieldValue('payment_value')
    const val = Number(parseFormattedNumberInput(rawValue))

    hasInputPaymentRef.current = true
    if (val) {
      setPaymentAmount(val)
      form.setFieldsValue({ payment_value: formatNumberV2(val, paymentDecimal) })
      setDirection(Direction.PAY_TO_BEN)
      setLastChange('SENDER')

      // Validate payment_value first
      try {
        await form.validateFields(['payment_value'])

        // If payment_value is valid, calculate beneficiary_value
        const calculatedBeneficiaryNum = val * exchangeRate
        const maxLimitBeneficiary = getMaxLimit(sendCurrency)

        preservedBeneficiaryErrorRef.current = null

        if (
          maxLimitBeneficiary !== null &&
          calculatedBeneficiaryNum > maxLimitBeneficiary
        ) {
          console.log(
            '[SendMoney] calculatedBeneficiaryNum > maxLimitBeneficiary',
            calculatedBeneficiaryNum,
            formatNumberV2(calculatedBeneficiaryNum, beneficiaryDecimal)
          )
          form.setFieldsValue({
            beneficiary_value: formatNumberV2(calculatedBeneficiaryNum, beneficiaryDecimal),
          })
          setBeneficiaryAmount(calculatedBeneficiaryNum)
          // Validate to show error - use multiple timeouts to catch configFee state updates
          const performValidation = async () => {
            try {
              await form.validateFields(['beneficiary_value'])
            } catch (error) {
              // Error is expected if validation fails
            }
          }
          setTimeout(() => {
            performValidation()
          }, 0)
          setTimeout(() => {
            performValidation()
          }, 200)
          setTimeout(() => {
            performValidation()
          }, 500)
        } else {
          console.log(
            '[SendMoney] calculatedBeneficiaryNum > maxLimitBeneficiary',
            calculatedBeneficiaryNum,
            formatNumberV2(calculatedBeneficiaryNum, beneficiaryDecimal)
          )
          form.setFieldsValue({
            beneficiary_value: formatNumberV2(calculatedBeneficiaryNum, beneficiaryDecimal),
          })
          setBeneficiaryAmount(calculatedBeneficiaryNum)

          // Validate beneficiary_value - use multiple timeouts to catch configFee state updates
          // This ensures validation runs after all state updates (configFee, transferFee, serviceFee)
          const performValidation = async () => {
            try {
              await form.validateFields(['beneficiary_value'])
            } catch (error) {
              // Error is expected if validation fails
            }
          }

          // Validate immediately
          setTimeout(() => {
            performValidation()
          }, 0)

          // Validate again after delays to catch configFee state updates
          setTimeout(() => {
            performValidation()
          }, 200)

          setTimeout(() => {
            performValidation()
          }, 500)
        }
      } catch (error) {
        // If payment_value has error, don't calculate beneficiary_value
        form.resetFields(['beneficiary_value'])
        form.setFields([{ name: 'beneficiary_value', errors: [] }])
        setBeneficiaryAmount(0)
      }
    } else {
      form.resetFields(['beneficiary_value'])
      setPaymentAmount(0)
      setBeneficiaryAmount(0)
    }
  }

  // Helper function to recalculate values based on lastChange
  const recalculateBasedOnLastChange = useCallback(() => {
    // In edit mode, block until the initial recalculation from API data is done.
    // After that, rate/fee poll changes trigger automatic recalculation.
    if (editData?.id && !hasRecalcPaymentFromEditRef.current) {
      return
    }

    // Don't recalculate if we're changing currency (format is already handled in handleChange functions)
    if (isChangingCurrencyRef.current) {
      return
    }
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    // Don't recalculate if both values are 0 or empty
    if (beneficiaryValueNum === 0 && paymentValueNum === 0) {
      return
    }

    // Helper to format beneficiary value: preserve decimal format if original has decimals
    const formatBeneficiaryValue = (value: number, originalValue: string) => {
      if (originalValue && typeof originalValue === 'string') {
        const trimmed = (parseFormattedNumberInput(originalValue) || '').trim()
        if (/^\d+$/.test(trimmed)) return formatNumberV2(value, 0)
      }
      return formatNumberV2(value, beneficiaryDecimal)
    }

    // If lastChange is BENEFICARY and beneficiary value exists, keep it and recalculate payment
    if (lastChange === 'BENEFICARY' && beneficiaryValueNum > 0) {
      const newPaymentValueNum = beneficiaryValueNum * (1 / exchangeRate)
      const newPaymentValue = formatNumberV2(newPaymentValueNum, paymentDecimal)

      form.setFieldsValue({
        beneficiary_value: formatBeneficiaryValue(
          beneficiaryValueNum,
          currentBeneficiaryValue
        ),
        payment_value: newPaymentValue,
      })
      setBeneficiaryAmount(beneficiaryValueNum)
      setPaymentAmount(newPaymentValueNum)
      setDirection(Direction.BEN_TO_PAY)

      // Validate payment_value after setting to show error if invalid
      setTimeout(() => {
        form.validateFields(['payment_value'])
      }, 0)
    }
    // If lastChange is SENDER and payment value exists, keep it and recalculate beneficiary
    else if (lastChange === 'SENDER' && paymentValueNum > 0) {
      const newBeneficiaryValueNum = paymentValueNum * exchangeRate
      const newBeneficiaryValue = formatNumberV2(newBeneficiaryValueNum, beneficiaryDecimal)

      // Check if recalculated beneficiary_value exceeds max limit
      const maxLimit = getMaxLimit(sendCurrency)
      if (maxLimit !== null && newBeneficiaryValueNum > maxLimit) {
        // Set the calculated value, don't clear payment_value
        form.setFieldsValue({
          beneficiary_value: newBeneficiaryValue,
          payment_value: formatNumberV2(paymentValueNum, paymentDecimal),
        })
        setBeneficiaryAmount(newBeneficiaryValueNum)
        setPaymentAmount(paymentValueNum)
        setDirection(Direction.PAY_TO_BEN)
        // Only trigger validation to show error, don't change values
        setTimeout(() => {
          form.validateFields(['beneficiary_value'])
        }, 0)
      } else {
        form.setFieldsValue({
          beneficiary_value: newBeneficiaryValue,
          payment_value: formatNumberV2(paymentValueNum, paymentDecimal),
        })
        setBeneficiaryAmount(newBeneficiaryValueNum)
        setPaymentAmount(paymentValueNum)
        setDirection(Direction.PAY_TO_BEN)

        // Validate both fields after setting to show errors if invalid
        setTimeout(() => {
          form.validateFields(['beneficiary_value', 'payment_value'])
        }, 0)
      }
    }
    // If both values exist, use direction to determine which to keep
    else if (beneficiaryValueNum > 0 && paymentValueNum > 0) {
      if (direction === Direction.BEN_TO_PAY) {
        const newPaymentValueNum = beneficiaryValueNum * (1 / exchangeRate)
        const newPaymentValue = formatNumberV2(newPaymentValueNum, paymentDecimal)

        form.setFieldsValue({
          beneficiary_value: formatBeneficiaryValue(
            beneficiaryValueNum,
            currentBeneficiaryValue
          ),
          payment_value: newPaymentValue,
        })
        setBeneficiaryAmount(beneficiaryValueNum)
        setPaymentAmount(newPaymentValueNum)

        // Validate payment_value after setting to show error if invalid
        setTimeout(() => {
          form.validateFields(['payment_value'])
        }, 0)
      } else {
        const newBeneficiaryValueNum = paymentValueNum * exchangeRate
        const newBeneficiaryValue = formatNumberV2(newBeneficiaryValueNum, beneficiaryDecimal)

        // Check if recalculated beneficiary_value exceeds max limit
        const maxLimit = getMaxLimit(sendCurrency)
        if (maxLimit !== null && newBeneficiaryValueNum > maxLimit) {
          // Set the calculated value, don't clear payment_value
          form.setFieldsValue({
            beneficiary_value: newBeneficiaryValue,
            payment_value: formatNumberV2(paymentValueNum, paymentDecimal),
          })
          setBeneficiaryAmount(newBeneficiaryValueNum)
          setPaymentAmount(paymentValueNum)
          // Only trigger validation to show error, don't change values
          setTimeout(() => {
            form.validateFields(['beneficiary_value'])
          }, 0)
        } else {
          form.setFieldsValue({
            beneficiary_value: newBeneficiaryValue,
            payment_value: formatNumberV2(paymentValueNum, paymentDecimal),
          })
          setBeneficiaryAmount(newBeneficiaryValueNum)
          setPaymentAmount(paymentValueNum)

          // Validate both fields after setting to show errors if invalid
          setTimeout(() => {
            form.validateFields(['beneficiary_value', 'payment_value'])
          }, 0)
        }
      }
    }
  }, [exchangeRate, paymentDecimal, beneficiaryDecimal, form, sendCurrency])

  // When user changes fee payer (Sender/Beneficiary pays), recalculate Beneficiary gets & Payment Amount with latest rate
  useEffect(() => {
    if (isInitialFeePayerRef.current) {
      if (!editData?.id) return
      if (!isInitialFeePayerRef.current) return
      const anyEdit: any = editData
      const feePayerFromEdit = anyEdit?.feePayer ?? anyEdit?.feesPayer

      const nextChecked =
        feePayerFromEdit === 'BENEFICIARY'
          ? listPayFee[1].value
          : listPayFee[0].value

      setChecked(nextChecked)
      isInitialFeePayerRef.current = false
      return
    }
    if (!exchangeRate || isChangingCurrencyRef.current) return
    const beneficiaryValueNum = Number(parseFormattedNumberInput(form.getFieldValue('beneficiary_value'))) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(form.getFieldValue('payment_value'))) || 0
    if (beneficiaryValueNum === 0 && paymentValueNum === 0) return
    recalculateBasedOnLastChange()
  }, [checked, editData?.id])

  useEffect(() => {
    if (loadingRate || !exchangeRate) {
      return
    }

    // Don't recalculate if we're setting default values
    if (isSettingDefaultRef.current) {
      return
    }

    // Don't recalculate during initial setup (before defaults have been set)
    if (!hasSetDefaultRef.current) {
      return
    }

    // // Don't recalculate during first calculation phase
    // if (firstCalculate) {
    //   console.log('[SendMoney] skip recalc on exchangeRate change: firstCalculate phase')
    //   return
    // }

    // Don't recalculate if we're changing currency (format is already handled in handleChange functions)
    if (isChangingCurrencyRef.current) {
      return
    }

    // If currencies are the same, exchangeRate = 1, and values are already correct
    // No need to recalculate to avoid "flickering"
    const isSameCurrency = sendCurrency === paymentCurrency
    if (isSameCurrency) {
      return
    }

    // Read current values from form to avoid dependency on state
    const currentBeneficiaryValue = form.getFieldValue('beneficiary_value')
    const currentPaymentValue = form.getFieldValue('payment_value')
    const beneficiaryValueNum = Number(parseFormattedNumberInput(currentBeneficiaryValue)) || 0
    const paymentValueNum = Number(parseFormattedNumberInput(currentPaymentValue)) || 0

    // Don't recalculate if both values are 0 or empty
    // Only recalculate if we have at least one non-zero value
    if (paymentValueNum === 0 && beneficiaryValueNum === 0) {
      return
    } else {
      // Recalculate based on lastChange when exchange rate changes
      // This keeps the value that was just entered and recalculates the other
      // If we're changing currency, this will recalculate with new exchangeRate
      recalculateBasedOnLastChange()

      // If we're changing currency, validate after recalculation
      if (isChangingCurrencyRef.current) {
        setTimeout(async () => {
          try {
            await form.validateFields(['beneficiary_value', 'payment_value'])
            isChangingCurrencyRef.current = false
          } catch (error) {
            // Get errors to ensure they are displayed
            const beneficiaryErrors = form.getFieldError('beneficiary_value')
            const paymentErrors = form.getFieldError('payment_value')

            // Keep the flag true a bit longer to prevent onValuesChange from clearing the error
            // Then reset it so user can interact normally
            setTimeout(() => {
              isChangingCurrencyRef.current = false

              // Re-validate to ensure errors are still displayed if values are still invalid
              const currentPaymentValue = form.getFieldValue('payment_value')
              const currentBeneficiaryValue =
                form.getFieldValue('beneficiary_value')
              if (currentPaymentValue) {
                form.validateFields(['payment_value'])
              }
              if (currentBeneficiaryValue) {
                form.validateFields(['beneficiary_value'])
              }
            }, 300)
          }
        }, 100)
      }
    }
  }, [
    exchangeRate,
    loadingRate,
    sendCurrency,
    paymentDecimal,
    beneficiaryDecimal,
    recalculateBasedOnLastChange,
    form,
    dataAllCurrency,
    firstCalculate,
    editData?.id,
  ])

  const handleBlurUID = async () => {
    const code = form.getFieldValue('smartUID')
      ? form.getFieldValue('smartUID')?.trim()
      : ''
    if (!code) {
      form.setFields([{ name: 'smartUID', errors: undefined }])
      setInfoByUserCode(null)
      return
    }
    await fetchInfoByUserCode(code)
  }

  useEffect(() => {
    const handleBalanceUpdate = () => {
      refetchBalance()
    }
    socket.on(`user.wallet.updated`, handleBalanceUpdate)

    return () => {
      socket.off(`user.wallet.updated`, handleBalanceUpdate)
    }
  }, [socket, refetchBalance])

  const handleTotalTopay = () => {
    if (checked === listPayFee[0].value) {
      if (transferFee === null) return `-`
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        Number((paymentAmount + transferFee + serviceFee).toFixed(paymentDecimal)),
        paymentDecimal
      )} ${paymentCurrencySymbol}`
    } else {
      if (
        roundTo(
          beneficiaryAmount -
          transferFee -
          serviceFee -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          Number(paymentAmount.toFixed(paymentDecimal)),
          paymentDecimal
        )} ${paymentCurrencySymbol}`
    }
  }

  const handleBeneficiaryGet = () => {
    if (checked === listPayFee[0].value) {
      if (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
        return `-`
      return `${formatNumber(
        Number(beneficiaryAmount.toFixed(beneficiaryDecimal)),
        beneficiaryDecimal
      )} ${sendCurrencySymbol}`
    } else {
      if (transferFee === null) return `-`
      if (
        roundTo(
          beneficiaryAmount -
          transferFee -
          serviceFee -
          configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate,
          beneficiaryDecimal
        ) < 0
      )
        return `-`
      else
        return `${formatNumber(
          Number(
            (beneficiaryAmount - transferFee - serviceFee).toFixed(
              beneficiaryDecimal
            )
          ),
          beneficiaryDecimal
        )} ${sendCurrencySymbol}`
    }
  }

  const isNegativeBeneficiaryReceive = () => {
    if (checked === listPayFee[0].value) {
      // For payer-fee option, "You Will Receive" shows beneficiaryAmount only
      // when buffer condition passes, so negative display should not occur.
      return (
        roundTo(
          paymentAmount -
          (configFee?.bufferUsd * configFee?.usdToFeeCurrencyRate) /
          exchangeRate,
          paymentDecimal
        ) < 0
      )
    }
    if (transferFee === null) return false
    // For the second option, displayed "You Will Receive" is:
    // beneficiaryAmount - transferFee - serviceFee
    // Disable when that displayed value would be negative.
    return (
      roundTo(
        beneficiaryAmount - transferFee - serviceFee,
        beneficiaryDecimal
      ) < 0
    )
  }

  const logoSelected = useCallback(() => {
    if (!paymentCurrency) return null
    const item = dataAllCurrency.find(
      (item: any) => item.id === paymentCurrency
    )
    return (
      <img
        src={`${process.env.REACT_APP_ROOT_URL}/${item?.iconPath}`}
        onError={(e) => {
          e.currentTarget.src = smartLogoDefault
        }}
        style={{
          objectFit: 'cover',
          width: 40,
          height: 40,
          borderRadius: '50%',
        }}
        alt='currency'
      />
    )
  }, [paymentCurrency, dataAllCurrency])

  const handleInsufficientBalanceConfirmation = () => {
    setShowBalanceConfirmation(false)
    if (dataEditCreateOrder) {
      if (orderId || editData?.id || detailId) {
        onUpdateOrder(dataEditCreateOrder)
      } else {
        onSubmitOrder(dataEditCreateOrder)
      }
    }
    setDataEditCreateOrder(null)
  }

  return (
    <Form
      form={form}
      onFinish={handleSubmitOrder}
      onValuesChange={(changedValues, allValues) => {
        const changedFieldName = Object.keys(changedValues)[0]

        // Don't clear validation errors when changing currency
        if (isChangingCurrencyRef.current) {
          return
        }

        // IMPORTANT: Never clear errors when changing currency fields
        // Currency changes will trigger their own validation logic
        if (
          changedFieldName === 'payment_currency' ||
          changedFieldName === 'beneficiary_currency'
        ) {
          // If payment_currency changed and we have a preserved error, ensure it's set
          if (
            changedFieldName === 'payment_currency' &&
            preservedBeneficiaryErrorRef.current
          ) {
            const currentErrors = form.getFieldError('beneficiary_value')
            if (currentErrors.length === 0) {
              // Set the preserved error to ensure it's displayed
              form.setFields([
                {
                  name: 'beneficiary_value',
                  errors: [preservedBeneficiaryErrorRef.current],
                },
              ])
            }
          }
          return
        }

        // Check if beneficiary_value or payment_value has errors
        // NEVER clear errors if field still has validation errors
        const beneficiaryErrors = form.getFieldError('beneficiary_value')
        const paymentErrors = form.getFieldError('payment_value')
        const hasBeneficiaryError = beneficiaryErrors.length > 0
        const hasPaymentError = paymentErrors.length > 0

        // If beneficiary_value or payment_value is being changed and has errors, don't clear
        if (
          changedFieldName === 'beneficiary_value' ||
          changedFieldName === 'payment_value'
        ) {
          if (hasBeneficiaryError || hasPaymentError) {
            return
          }
        }

        // If changing other fields, check if beneficiary_value or payment_value has errors
        // If they have errors, don't clear them (only clear errors for the changed field if it's not beneficiary_value or payment_value)
        if (hasBeneficiaryError || hasPaymentError) {
          // Only clear errors for fields that are not beneficiary_value or payment_value
          const fieldsToClear: string[] = []
          Object.keys(changedValues).forEach((fieldName) => {
            if (
              fieldName !== 'beneficiary_value' &&
              fieldName !== 'payment_value'
            ) {
              fieldsToClear.push(fieldName)
            }
          })
          if (fieldsToClear.length > 0) {
            // Only clear errors for non-beneficiary/payment fields
            const filteredChangedValues: any = {}
            fieldsToClear.forEach((fieldName) => {
              filteredChangedValues[fieldName] = changedValues[fieldName]
            })
            clearFieldErrorMessageOnChange(form)(filteredChangedValues)
          }
          return
        }

        clearFieldErrorMessageOnChange(form)(changedValues)
      }}
      initialValues={{
        send_date: dayjs(),
        special_intructions: specialIntructionsDraft,
      }}>
      <Row gutter={{ md: 40 }}>
        <Col xs={24} md={14}>
          <div
            style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <div className='send-money-frame-detail'>
              <Typography className='title-detail'>Transfer Details</Typography>
              <div className='input-select'>
                <Form.Item
                  label='Beneficiary gets'
                  name='beneficiary_value'
                  style={{ width: '60%' }}
                  validateTrigger='onBlur'
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (!hasInputBeneficiaryRef.current && !hasInputPaymentRef.current && !hasChangingCurrencyRef.current) {
                          return Promise.resolve()
                        }
                        if (!value) return Promise.resolve()
                        const rawValue = parseFormattedNumberInput(value)
                        const numValue = Number(rawValue)
                        if (Number.isNaN(numValue) || numValue <= 0) {
                          return Promise.reject(
                            new Error('The amount must be greater than 0')
                          )
                        }

                        const maxLimit = getMaxLimit(sendCurrency)

                        if (maxLimit !== null && numValue > maxLimit) {
                          return Promise.reject(
                            new Error(
                              `Amount exceeds the maximum allowed limit of ${formatNumber(
                                maxLimit,
                                beneficiaryDecimal
                              )}`
                            )
                          )
                        }

                        // case: sender pay fee
                        if (checked === listPayFee[0].value) {
                          // Skip min amount validation if configFee is not available or loading
                          // This prevents validation with old fee data when currency is changed
                          if (!configFee || loadingFee) {
                            // Use preserved error if:
                            // 1. We're actively changing currency, OR
                            // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                            // This ensures errors are preserved during the entire currency change process
                            if (
                              preservedBeneficiaryErrorRef.current &&
                              (isChangingCurrencyRef.current || !configFee)
                            ) {
                              return Promise.reject(
                                new Error(preservedBeneficiaryErrorRef.current)
                              )
                            }
                            // If there's an existing error, keep it (don't clear)
                            // This prevents clearing errors when configFee is temporarily null during currency change
                            const currentErrors =
                              form.getFieldError('beneficiary_value')
                            if (currentErrors.length > 0) {
                              // Return the existing error to preserve it
                              return Promise.reject(new Error(currentErrors[0]))
                            }
                            // If not changing currency and configFee is not available, trigger a fetch
                            // This ensures configFee is available for validation
                            if (
                              !isChangingCurrencyRef.current &&
                              !configFee &&
                              sendCurrency
                            ) {
                              fetchFee(sendCurrency)
                            }
                            // Return resolve to allow validation to retry when configFee is available
                            // The re-validation useEffect will trigger validation again when configFee is fetched
                            return Promise.resolve()
                          }

                          const minBeneficiary = roundTo(
                            configFee?.bufferUsd *
                            configFee?.usdToFeeCurrencyRate,
                            beneficiaryDecimal
                          )

                          if (numValue < minBeneficiary) {
                            setBeneficiaryValueError(true)
                            const errorMsg = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
                            return Promise.reject(new Error(errorMsg))
                          }
                        }

                        // case: beneficiary pay fee
                        if (checked === listPayFee[1].value) {
                          // Skip min amount validation if configFee is not available or loading
                          // This prevents validation with old fee data when currency is changed
                          if (!configFee || loadingFee) {
                            // Use preserved error if:
                            // 1. We're actively changing currency, OR
                            // 2. configFee is null/loading and we have a preserved error (currency was just changed)
                            // This ensures errors are preserved during the entire currency change process
                            if (
                              preservedBeneficiaryErrorRef.current &&
                              (isChangingCurrencyRef.current || !configFee)
                            ) {
                              return Promise.reject(
                                new Error(preservedBeneficiaryErrorRef.current)
                              )
                            }
                            // If there's an existing error, keep it (don't clear)
                            const currentErrors =
                              form.getFieldError('beneficiary_value')
                            if (currentErrors.length > 0) {
                              return Promise.reject(new Error(currentErrors[0]))
                            }
                            return Promise.resolve()
                          }

                          // If transferFee or serviceFee is null, skip validation to avoid false positives
                          // The validation will be triggered again when fees are calculated
                          if (transferFee === null || serviceFee === null) {
                            // Use preserved error if:
                            // 1. We're actively changing currency, OR
                            // 2. Fees are null and we have a preserved error (currency was just changed)
                            if (
                              preservedBeneficiaryErrorRef.current &&
                              (isChangingCurrencyRef.current ||
                                transferFee === null ||
                                serviceFee === null)
                            ) {
                              return Promise.reject(
                                new Error(preservedBeneficiaryErrorRef.current)
                              )
                            }
                            // If there's an existing error, keep it (don't clear)
                            const currentErrors =
                              form.getFieldError('beneficiary_value')
                            if (currentErrors.length > 0) {
                              return Promise.reject(new Error(currentErrors[0]))
                            }
                            // Don't validate yet, wait for fees to be calculated
                            // Return resolve to avoid blocking, but validation will be retried when fees are ready
                            return Promise.resolve()
                          }

                          // const minBeneficiary = roundTo(
                          //   transferFee +
                          //   serviceFee +
                          //   configFee?.bufferUsd *
                          //   configFee?.usdToFeeCurrencyRate,
                          //   beneficiaryDecimal
                          // )
                          const minBeneficiary = roundTo(
                            transferFee +
                            serviceFee +
                            configFee?.bufferUsd *
                            configFee?.usdToFeeCurrencyRate,
                            beneficiaryDecimal
                          )
                          if (numValue < minBeneficiary) {
                            setBeneficiaryValueError(true)
                            const errorMsg = `Your beneficiary get must be at least ${formatNumber(minBeneficiary, beneficiaryDecimal)}`
                            return Promise.reject(new Error(errorMsg))
                          }
                        }

                        setBeneficiaryValueError(false)
                        // Clear preserved error since validation passed
                        preservedBeneficiaryErrorRef.current = null
                        return Promise.resolve()
                      },
                    },
                  ]}>
                  <Input
                    onChange={handleChangeBeneficiaryValue}
                    onBlur={handleBlurSend}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        'Backspace',
                        'Delete',
                        'ArrowLeft',
                        'ArrowRight',
                        'Tab',
                      ]
                      if (allowedKeys.includes(e.key)) return

                      // Only allow numbers or decimal point
                      if (!/^[0-9.]$/.test(e.key)) {
                        e.preventDefault()
                        return
                      }

                      const { value, selectionStart, selectionEnd } =
                        e.currentTarget

                      // Only allow one decimal point
                      if (e.key === '.' && value?.includes('.')) {
                        e.preventDefault()
                        return
                      }

                      // Check number of decimal places
                      if (value?.includes('.')) {
                        const dotIndex = value?.indexOf('.')
                        const decimals = value?.substring(dotIndex + 1)

                        // If typing after decimal point, check length
                        if (
                          selectionStart !== null &&
                          selectionStart > dotIndex &&
                          decimals.length >= beneficiaryDecimal
                        ) {
                          e.preventDefault()
                        }
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name={'beneficiary_currency'}
                  style={{
                    width: '40%',
                    marginTop: isTablet ? '22px' : '32px',
                  }}>
                  <Select
                    suffixIcon={<ArrowIcon />}
                    popupClassName='custom-select-dropdown'
                    onChange={(value) => handleChangeSendCurrency(value)}
                    className='select-send-money'>
                    <OptGroup key='Fiat' label='Fiat'>
                      {dataAllCurrency.map((item: any) => {
                        if (item.type === 'FIAT')
                          return (
                            <Option key={item.id} value={item.id}>
                              <div className='currency-option'>
                                <img
                                  src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                  onError={(e) => {
                                    e.currentTarget.src = smartLogoDefault
                                  }}
                                  style={{
                                    objectFit: 'cover',
                                    width: isTablet ? 24 : 32,
                                    height: isTablet ? 24 : 32,
                                    borderRadius: '50%',
                                  }}
                                  alt='currency'
                                />
                                <span>{item.symbol}</span>
                              </div>
                              {sendCurrency === item.id && <CheckIcon />}
                            </Option>
                          )
                      })}
                    </OptGroup>
                    <OptGroup key='Crypto' label='Crypto'>
                      {dataAllCurrency.map((item: any) => {
                        if (item.type === 'CRYPTO')
                          return (
                            <Option key={item.id} value={item.id}>
                              <div className='currency-option'>
                                <img
                                  src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                  onError={(e) => {
                                    e.currentTarget.src = smartLogoDefault
                                  }}
                                  style={{
                                    objectFit: 'cover',
                                    width: isTablet ? 24 : 32,
                                    height: isTablet ? 24 : 32,
                                    borderRadius: '50%',
                                  }}
                                  alt='currency'
                                />
                                <span>{item.symbol}</span>
                              </div>
                              {sendCurrency === item.id && <CheckIcon />}
                            </Option>
                          )
                      })}
                    </OptGroup>
                  </Select>
                </Form.Item>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10
                }}
              >
                <div className='input-select'>
                  <Form.Item
                    label='Payment Amount'
                    name='payment_value'
                    style={{ width: '60%' }}
                    validateTrigger='onBlur'
                    rules={[
                      { required: true, message: 'This field is required' },
                      {
                        validator: async (_, value) => {
                          if (!hasInputPaymentRef.current && !hasInputBeneficiaryRef.current && !hasChangingCurrencyRef.current) {
                            return Promise.resolve()
                          }
                          if (!value) return Promise.resolve()

                          const rawValue = parseFormattedNumberInput(value)
                          if (
                            value === '.' ||
                            (rawValue.endsWith('.') && !rawValue.startsWith('.'))
                          ) {
                            return Promise.resolve()
                          }

                          const numValue = Number(rawValue)
                          if (Number.isNaN(numValue) || numValue <= 0) {
                            return Promise.reject(
                              new Error('The amount must be greater than 0')
                            )
                          }

                          // Max limit check theo currency - use form field value to ensure we use the latest currency
                          const currentPaymentCurrency =
                            form.getFieldValue('payment_currency') ||
                            paymentCurrency
                          const maxLimit = getMaxLimit(currentPaymentCurrency)

                          if (maxLimit !== null && numValue > maxLimit) {
                            return Promise.reject(
                              new Error(
                                `Amount exceeds the maximum allowed limit of ${formatNumber(
                                  maxLimit,
                                  paymentDecimal
                                )}`
                              )
                            )
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}>
                    <Input
                      onChange={handleChangePaymentValue}
                      onBlur={handleBlurPayment}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'ArrowLeft',
                          'ArrowRight',
                          'Tab',
                        ]
                        if (allowedKeys?.includes(e.key)) return

                        // Only allow numbers or decimal point
                        if (!/^[0-9.]$/.test(e.key)) {
                          e.preventDefault()
                          return
                        }

                        const { value, selectionStart, selectionEnd } =
                          e.currentTarget

                        // Only allow one decimal point
                        if (e.key === '.' && value?.includes('.')) {
                          e.preventDefault()
                          return
                        }

                        // Check number of decimal places
                        if (value?.includes('.')) {
                          const dotIndex = value?.indexOf('.')
                          const decimals = value?.substring(dotIndex + 1)

                          // If typing after decimal point, check length
                          if (
                            selectionStart !== null &&
                            selectionStart > dotIndex &&
                            decimals.length >= paymentDecimal
                          ) {
                            e.preventDefault()
                          }
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={'payment_currency'}
                    style={{
                      width: '40%',
                      marginTop: isTablet ? '22px' : '32px',
                    }}>
                    <Select
                      suffixIcon={<ArrowIcon />}
                      popupClassName='custom-select-dropdown'
                      onChange={(value) => handleChangePaymentCurrency(value)}
                      className='select-send-money'>
                      <OptGroup key='Fiat' label='Fiat'>
                        {dataAllCurrency.map((item: any) => {
                          if (item.type === 'FIAT')
                            return (
                              <Option key={item.id} value={item.id}>
                                <div className='currency-option'>
                                  <img
                                    src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                    onError={(e) => {
                                      e.currentTarget.src = smartLogoDefault
                                    }}
                                    style={{
                                      objectFit: 'cover',
                                      width: isTablet ? 24 : 32,
                                      height: isTablet ? 24 : 32,
                                      borderRadius: '50%',
                                    }}
                                    alt='currency'
                                  />
                                  <span>{item.symbol}</span>
                                </div>
                                {paymentCurrency === item.id && <CheckIcon />}
                              </Option>
                            )
                        })}
                      </OptGroup>
                      <OptGroup key='Crypto' label='Crypto'>
                        {dataAllCurrency.map((item: any) => {
                          if (item.type === 'CRYPTO')
                            return (
                              <Option key={item.id} value={item.id}>
                                <div className='currency-option'>
                                  <img
                                    src={`${process.env.REACT_APP_ROOT_URL}/${item.iconPath}`}
                                    onError={(e) => {
                                      e.currentTarget.src = smartLogoDefault
                                    }}
                                    style={{
                                      objectFit: 'cover',
                                      width: isTablet ? 24 : 32,
                                      height: isTablet ? 24 : 32,
                                      borderRadius: '50%',
                                    }}
                                    alt='currency'
                                  />
                                  <span>{item.symbol}</span>
                                </div>
                                {paymentCurrency === item.id && <CheckIcon />}
                              </Option>
                            )
                        })}
                      </OptGroup>
                    </Select>
                  </Form.Item>
                </div>
                {sendCurrency &&
                  paymentCurrency &&
                  exchangeRate &&
                  (() => {
                    const rateValue = 1 / exchangeRate
                    const { value: roundedValue, decimals: finalDecimals } =
                      roundDownWithMinimum(rateValue, paymentDecimal)
                    return (
                      <Typography
                        style={{
                          fontSize: '12px',
                          fontWeight: 400,
                          color: 'rgba(242, 138, 40, 1)',
                        }}>
                        Indicated rate (1 minute): 1 {sendCurrencySymbol} ≈{' '}
                        {formatNumber(roundedValue, finalDecimals)}{' '}
                        {paymentCurrencySymbol}
                      </Typography>
                    )
                  })()}
              </div>

              <SelectBeneficiary
                form={form}
                isTablet={isTablet}
                listBeneficiary={listBeneficiary}
                beneficiarySelected={beneficiarySelected}
                setBeneficiarySelected={setBeneficiarySelected}
                setWalletSelected={setWalletSelected}
                setBankAccountSelected={setBankAccountSelected}
                paramsBeneficiary={paramsBeneficiary}
                setParamsBeneficiary={setParamsBeneficiary}
                setStepNewBeneficiary={setStepNewBeneficiary}
                handleChangeBeneficiary={handleChangeBeneficiary}
              />
              {beneficiarySelected && (
                <>
                  <div className='transfer-mode'>
                    <Typography className='transfer-mode-text'>
                      Transfer Method
                    </Typography>
                    <div className='transfer-mode-list-button'>
                      {method === listMethod.FIAT_FIAT ? (
                        <ButtonTransferMode
                          size={isTablet ? 'small' : 'medium'}
                          active={
                            transferMode === listTransferMode.BANK_TRANSFER &&
                            beneficiary
                          }
                          text='Bank Transfer'
                          width={'fit-content'}
                          onClick={() => {
                            setTransferMode(listTransferMode.BANK_TRANSFER)
                            form?.resetFields([
                              'smartUID',
                              'beneficiary_wallet_address',
                            ])
                            setInfoByUserCode(null)
                            setBankAccountSelected(null)
                            setWalletSelected(null)
                          }}
                          disabled={!beneficiary}
                        />
                      ) : null}
                      {method === listMethod.CRYPTO_CRYPTO ? (
                        <ButtonTransferMode
                          size={isTablet ? 'small' : 'medium'}
                          active={
                            transferMode === listTransferMode.CRYPTO_NETWORK &&
                            beneficiary
                          }
                          text='Crypto Network'
                          width={'fit-content'}
                          onClick={() => {
                            setTransferMode(listTransferMode.CRYPTO_NETWORK)
                            form?.resetFields([
                              'smartUID',
                              'beneficiary_bank_account',
                            ])
                            setInfoByUserCode(null)
                            setBankAccountSelected(null)
                            setWalletSelected(null)
                          }}
                          disabled={!beneficiary}
                        />
                      ) : null}
                      {beneficiarySelected?.refUser?.smartUID ? (
                        <ButtonTransferMode
                          size={isTablet ? 'small' : 'medium'}
                          active={
                            transferMode === listTransferMode.INTERNAL_TRANSFER
                          }
                          text='SMART Wallet'
                          width={'fit-content'}
                          onClick={() => {
                            setTransferMode(listTransferMode.INTERNAL_TRANSFER)
                            form?.resetFields([
                              'beneficiary_wallet_address',
                              'beneficiary_bank_account',
                            ])
                            setInfoByUserCode(null)
                            setBankAccountSelected(null)
                            setWalletSelected(null)
                          }}
                          disabled={!beneficiary}
                        />
                      ) : null}
                    </div>
                    {transferMode === listTransferMode.INTERNAL_TRANSFER &&
                      !beneficiarySelected?.refUser?.smartUID ? (
                      <Typography
                        style={{
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#ff4d4f',
                        }}>
                        Please select or create a beneficiary that has a SMART
                        UID
                      </Typography>
                    ) : null}
                  </div>
                  {transferMode === listTransferMode.CRYPTO_NETWORK ? (
                    <SelectWallet
                      isTablet={isTablet}
                      form={form}
                      listWallet={listWallet}
                      walletSelected={walletSelected}
                      setWalletSelected={setWalletSelected}
                      paramsWallet={paramsWallet}
                      setParamsWallet={setParamsWallet}
                      setOpenAddWallet={setOpenAddWallet}
                      loading={loadingListWallet}
                    />
                  ) : transferMode === listTransferMode.BANK_TRANSFER ? (
                    <SelectBankAccount
                      isTablet={isTablet}
                      form={form}
                      listBankAccount={listBankAccount}
                      bankAccountSelected={bankAccountSelected}
                      setBankAccountSelected={setBankAccountSelected}
                      paramsBankAccount={paramsBankAccount}
                      setParamsBankAccount={setParamsBankAccount}
                      setOpenAddBankAccount={setOpenAddBankAccount}
                      loading={loadingListBankAccount}
                    />
                  ) : (
                    <>
                      {beneficiarySelected?.refUser?.smartUID ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            width: '100%',
                          }}>
                          <Form.Item
                            style={{ width: '100%' }}
                            label='SMART UID'
                            validateTrigger='onBlur'
                            name='smartUID'
                            rules={[
                              {
                                required: true,
                                message: 'This field is required',
                              },
                              {
                                max: 256,
                                message: 'Maximum 256 characters',
                                validateTrigger: 'onBlur',
                              },
                            ]}
                            className='input-user-id'>
                            <Input
                              onBlur={handleBlurUID}
                              disabled
                              placeholder='Enter the code linked to your SMART account'
                            />
                          </Form.Item>
                          {form.getFieldValue('beneficiary') ? null : (
                            <Typography
                              style={{
                                fontSize: isTablet ? 10 : 12,
                                fontWeight: 400,
                                color: '#EC3333',
                              }}>
                              {
                                'Please select a beneficiary before choosing a SMART UID.'
                              }
                            </Typography>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
              <div className='input-send-date'>
                <Form.Item
                  style={{ width: '100%' }}
                  label='Send Date'
                  name='send_date'
                  validateTrigger='onBlur'
                  rules={[
                    { required: true, message: 'This field is required' },
                  ]}>
                  <DatePicker
                    ref={datePickerRef}
                    placeholder='Select date'
                    className='custom-input-date send-money'
                    format={'DD/MM/YYYY'}
                    onChange={(
                      value: dayjs.Dayjs | null,
                      dateString: string | string[]
                    ) => {
                      handleSearchDate(value, dateString)
                      if (!value) {
                        setTimeout(() => {
                          datePickerRef.current?.focus?.()
                        })
                      }
                    }}
                    clearIcon={<ClearIcon style={{ height: 12, width: 12 }} />}
                    suffixIcon={searchDate ? null : <CalenderIcon />}
                    allowClear
                    disabledDate={(current) => {
                      return current && current < dayjs().startOf('day')
                    }}
                  />
                </Form.Item>
              </div>
              <Form.Item
                style={{ width: '100%' }}
                label='Purpose of Remittance'
                name='purposeOfRemittance'
                validateTrigger='onBlur'
                rules={[{ required: true, message: 'This field is required' }]}>
                <Select
                  className='select-recipient'
                  popupClassName='select-beneficiary'
                  placeholder='Purpose of Remitter'
                  suffixIcon={<ArrowIcon />}
                  optionLabelProp='label'
                  onChange={(value) => setPurpose(value)}>
                  {optionPurpostOfRemitter.map((item: any) => (
                    <Option
                      key={item.value}
                      value={item.value}
                      label={item.label}>
                      <span style={{ fontWeight: 400, fontSize: '14px' }}>
                        {item?.label}
                      </span>
                      {purpose === item?.value && <CheckSelectIcon />}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                style={{ width: '100%' }}
                label='Special instructions'
                name='special_intructions'
                validateTrigger='onBlur'
                rules={[
                  {
                    max: 256,
                    message: 'Maximum 256 characters',
                    validateTrigger: 'onBlur',
                  },
                ]}>
                <TextArea
                  className='sendmoney-address'
                  placeholder='Enter a note'
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  onChange={(e) => {
                    const currentValue = e.target.value
                    const idUserPrefix = specialIntructionsDraft || ''
                    if (!currentValue.startsWith(idUserPrefix)) {
                      const finalValue = idUserPrefix
                      form.setFieldValue('special_intructions', finalValue)
                    } else {
                      form.setFieldValue('special_intructions', currentValue)
                    }
                  }}
                />
              </Form.Item>
              <div>
                <Form.Item label='Supporting Document' name='supportDocument'>
                  <Upload
                    disabled={document?.fileList.length === 5}
                    multiple
                    customRequest={({ file, onSuccess, onError }) => {
                      const formData = new FormData()
                      formData.append('file', file as RcFile)

                      fetch(`${ROOT_URL}/api/v1/storage/upload`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem(
                            TOKEN_KEY
                          )}`,
                        },
                        body: formData,
                      })
                        .then((res) => res.text())
                        .then((resString) => {
                          onSuccess?.(resString)
                        })
                        .catch((err) => onError?.(err))
                    }}
                    showUploadList={false}
                    fileList={document?.fileList}
                    onChange={(file) => handleChangeFile(file)}
                    accept={acceptUploadFileTypes}
                    beforeUpload={beforeUpload}>
                    <div
                      className='upload-file'
                      style={{
                        cursor:
                          document?.fileList.length === 5
                            ? 'default'
                            : 'pointer',
                      }}>
                      <img
                        src={
                          document?.fileList.length === 5
                            ? CloudArrowUpDisabled
                            : CloudArrowUp
                        }
                        style={{ width: 24, height: 24 }}
                      />
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color:
                            document?.fileList.length === 5
                              ? 'rgba(255, 255, 255, 0.15'
                              : 'white',
                        }}>
                        Upload file
                      </Typography>
                    </div>
                  </Upload>
                </Form.Item>
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 400,
                    color: 'rgba(242, 138, 40, 1)',
                    marginTop: '5px',
                  }}>
                  {'*Maximum 5 files allowed'}
                </Typography>
                <div className='list-file-upload'>
                  <Row gutter={{ md: 10 }}>
                    {Array.isArray(document?.fileList) &&
                      document?.fileList?.map(
                        (item: UploadFile, index: number) => (
                          <Col
                            key={index}
                            xs={24}
                            lg={12}
                            style={{ marginBottom: '10px' }}>
                            <div className='list-file-upload-item-container'>
                              <Typography
                                style={{
                                  fontSize: 16,
                                  fontWeight: 400,
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  direction: 'ltr',
                                  textAlign: 'left',
                                }}>
                                {item.name}
                              </Typography>
                              <TrashIcon
                                className='icon-animation trans-icon'
                                onClick={() => handleRemoveFile(item)}
                              />
                            </div>
                          </Col>
                        )
                      )}
                  </Row>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={24} md={10}>
          <div
            style={{ display: 'flex', justifyContent: 'start', width: '100%' }}>
            <div className='send-money-frame-payment'>
              <Typography className='title-payment'>Payment</Typography>
              <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
                {listPayFee.map((item) => (
                  <RadioCustom
                    key={item.value}
                    checked={item.value === checked}
                    content={item.label}
                    onClick={() => setChecked(item.value)}
                  />
                ))}
              </div>
              <div className='payment-fee'>
                <TextFee
                  title={'Transfer fee'}
                  value={
                    transferFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(
                          transferFee.toFixed(
                            checked === listPayFee[0].value
                              ? paymentDecimal
                              : beneficiaryDecimal
                          )
                        ),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : sendCurrencySymbol
                      }`
                  }
                />
                <TextFee
                  title={'Service fee'}
                  value={
                    serviceFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(
                          serviceFee.toFixed(
                            checked === listPayFee[0].value
                              ? paymentDecimal
                              : beneficiaryDecimal
                          )
                        ),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : sendCurrencySymbol
                      }`
                  }
                />
                <TextFee
                  title={'Total fee'}
                  value={
                    transferFee === null || serviceFee === null
                      ? `-`
                      : `${formatNumber(
                        Number(
                          (serviceFee + transferFee).toFixed(
                            checked === listPayFee[0].value
                              ? paymentDecimal
                              : beneficiaryDecimal
                          )
                        ),
                        checked === listPayFee[0].value
                          ? paymentDecimal
                          : beneficiaryDecimal
                      )} ${checked === listPayFee[0].value
                        ? paymentCurrencySymbol
                        : sendCurrencySymbol
                      }`
                  }
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    width: '100%',
                  }}>
                  <div className='total-to-pay'>
                    <Typography
                      style={{
                        fontSize: isTablet ? 16 : 20,
                        fontWeight: 700,
                        color: 'white',
                        minWidth: '150px',
                      }}>
                      Total to pay
                    </Typography>
                    <Typography.Text className='text-gradient'>
                      {handleTotalTopay()}
                    </Typography.Text>
                  </div>
                  <Typography.Text
                    style={{
                      fontSize: isTablet ? 14 : 16,
                      fontWeight: 400,
                      color: 'white',
                      textAlign: 'center',
                    }}>
                    Beneficiary Receive:{' '}
                    <Typography.Text
                      style={{
                        fontSize: isTablet ? 14 : 16,
                        fontWeight: 400,
                        color: 'rgba(242, 138, 40, 1)',
                        textAlign: 'center',
                      }}>
                      {handleBeneficiaryGet()}
                    </Typography.Text>
                  </Typography.Text>
                </div>
              </div>
              <div className='payment-method'>
                <div
                  className={'payment-method-border-v2'}
                  onClick={() => {
                    setIsFirstLoadBalance(false)
                  }}>
                  <div
                    className='d-flex'
                    style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    {logoSelected()}
                    <div className='balance-porfolio'>
                      <Typography
                        style={{
                          fontSize: isTablet ? 14 : 16,
                          fontWeight: 600,
                          color: 'white',
                        }}>
                        {paymentCurrencySymbol}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: isTablet ? 10 : 12,
                          color: 'rgba(255, 255, 255, 0.35)',
                        }}>
                        Portfolio
                      </Typography>
                    </div>
                  </div>
                  <div className='balance-porfolio'>
                    <Typography
                      style={{
                        fontSize: isTablet ? 12 : 14,
                        fontWeight: 600,
                        color: disabledPortfolio ? '#EC3333' : 'white',
                        textAlign: 'right',
                      }}>
                      {formatNumber(
                        avlBalance?.data?.data?.totalBalance || 0,
                        paymentDecimal
                      )}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: isTablet ? 10 : 12,
                        color: 'rgba(255, 255, 255, 0.35)',
                      }}>
                      Available Balance
                    </Typography>
                  </div>
                </div>
                <div
                  className={'director-transfer-border'}
                  onClick={() => {
                    setIsFirstLoadBalance(false)
                  }}>
                  <div className='director-transfer-content'>
                    <span className='title'>Provider</span>
                    <Popover
                      content={
                        <CompanyDefault
                          setOpenCompanyDefault={setOpenCompanyDefault}
                          dataCompaniesDefault={dataCompaniesDefault}
                          companyDefaultSelected={companyDefaultSelected}
                          loadingDefaulCompany={loadingDefaulCompany}
                          setSearchDefaultCompany={setSearchDefaultCompany}
                          onChange={(item: any) => {
                            setCompanyDefaultSelected(item)
                            setBankAccountSelectedProvider(null)
                            setWalletSelectedProvider(null)
                            form?.setFieldValue('companies_default', item?.id)
                            form?.resetFields([
                              'providerWallet',
                              'providerBankAccount',
                            ])
                          }}
                        />
                      }
                      trigger='click'
                      arrow={false}
                      overlayInnerStyle={{ borderRadius: '2px', padding: 0 }}
                      placement={'bottomRight'}
                      open={openCompanyDefault}
                      onOpenChange={onOpenChange}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '151px',
                          height: '24px',
                          padding: '3px 12px',
                          border: `1px solid ${disabledProvider
                            ? 'rgba(255, 255, 255, 0.15)'
                            : '#1DABF1'
                            }`,
                          borderRadius: '8px',
                          cursor: disabledProvider ? 'default' : 'pointer',
                        }}>
                        <Typography
                          className='company_default-text'
                          style={{
                            color: disabledProvider
                              ? 'rgba(255, 255, 255, 0.15)'
                              : '#1DABF1',
                          }}>
                          {companyDefaultSelected?.name || 'Select a provider'}
                        </Typography>
                        <CaretDown />
                      </div>
                    </Popover>
                  </div>
                  {form?.getFieldValue('companies_default') ? (
                    <>
                      {(
                        dataAllCurrency.find(
                          (item: any) => item.id === paymentCurrency
                        ) as any
                      )?.type === 'FIAT' ? (
                        <SelectBankAccountProvider
                          isTablet={isTablet}
                          form={form}
                          listBankAccountProvider={listBankAccountProvider}
                          bankAccountSelectedProvider={
                            bankAccountSelectedProvider
                          }
                          setBankAccountSelectedProvider={
                            setBankAccountSelectedProvider
                          }
                          paramsBankAccountProvider={paramsBankAccountProvider}
                          setParamsBankAccountProvider={
                            setParamsBankAccountProvider
                          }
                          companyDefaultSelected={companyDefaultSelected}
                        />
                      ) : (
                        <SelectWalletProvider
                          isTablet={isTablet}
                          form={form}
                          listWalletProvider={listWalletProvider}
                          walletSelectedProvider={walletSelectedProvider}
                          setWalletSelectedProvider={setWalletSelectedProvider}
                          paramsWalletProvider={paramsWalletProvider}
                          setParamsWalletProvider={setParamsWalletProvider}
                          companyDefaultSelected={companyDefaultSelected}
                        />
                      )}
                    </>
                  ) : null}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12
                }}
              >
                <CustomButton
                  type='submit'
                  text={editData?.id ? 'Update Order' : 'Create Order'}
                  size={isTablet ? 'medium' : 'large'}
                  width={'100%'}
                  disabled={
                    loadingCreate ||
                    loadingDetail ||
                    loadingFee ||
                    loadingUserCode ||
                    beneficiaryValueError ||
                    paymentValueError ||
                    loadingUpdate ||
                    isNegativeBeneficiaryReceive()
                  }
                />
                <Typography
                  style={{
                    fontSize: isTablet ? 10 : 12,
                    fontWeight: 400,
                    color: 'rgba(242, 138, 40, 1)',
                  }}>
                  {
                    '*the rate is applicable for 1 minute, please submit order within 1 minute.'
                  }
                </Typography>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <LoadingOverlay
        loading={loadingCreate || loadingDetail || loadingDefaultValue || loadingUpdate}
      />
      <ConfirmModal
        type='warning'
        title='Please Confirm Changes'
        description='Fee or beneficiary receive has been updated. Take a moment to review before continuing.'
        open={showReviewFee}
        onCancel={() => null}
        onSubmit={handleContinueReview}
        isLoading={false}
        textSubmit='OK'
        showButtonCancel={false}
      />
      <ConfirmModal
        type='warning'
        title='Insufficient Balance Confirmation'
        contentCustom={
          <>
            <Typography className='modal-text-description'>
              Your payment currency balance for this request is insufficient.
            </Typography>
            <Typography
              className='modal-text-description'
              style={{ marginTop: 16 }}>
              Submitting your order authorises the Provider to use other
              available assets in your portfolio to process the request.
              Applicable exchange rates and fees will apply.
            </Typography>
          </>
        }
        open={showBalanceConfirmation}
        onCancel={() => {
          setShowBalanceConfirmation(false)
          setDataEditCreateOrder(null)
        }}
        onSubmit={handleInsufficientBalanceConfirmation}
        isLoading={
          loadingCreate || loadingDetail || loadingFee || loadingUserCode
        }
        textSubmit='Submit'
        showButtonCancel={true}
        className='warning-modal'
      />
    </Form>
  )
}

export default FormTransferV2
